import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  Slide,
  Backdrop,
  IconButton
} from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
// import { Comment } from '@mui/icons-material'; // Replaced with custom icon
import { Home, Favorite, Share, Close } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useSiteConfig } from '../utils/useSiteConfig';
import CommentBottomSheet from '../components/CommentBottomSheet';
import BlogElementRenderer from '../components/BlogElementRenderer';
import AdSenseWidget from '../components/AdSenseWidget';
import sessionTracking from '../utils/sessionTracking';
import {
  config,
  LOGO_ICON_PATH,
  JOIN_LOGO_PATH,
  BRAND_NAME,
  BIG_LOGO_PATH,
  ABOUT_DESCRIPTION,
  ABOUT_BRANDS_INTRO,
  ABOUT_CONTACT_EMAIL,
  ABOUT_INFLUENCERS_CTA,
  HAS_CUSTOM_ABOUT_DESCRIPTION,
} from '../config';

const MotionBox = motion(Box);

/** Snappy horizontal page slide (“whoosh”) — left/right swipes and segment nav buttons */
const HORIZONTAL_PAGE_WHOOSH = {
  type: 'tween',
  duration: 0.4,
  ease: [0.16, 1, 0.3, 1]
};

/**
 * Vertical blog-to-blog whoosh — same easing as horizontal but slightly
 * faster to feel snappy on the Y axis.
 */
const VERTICAL_BLOG_WHOOSH = {
  type: 'tween',
  duration: 0.35,
  ease: [0.16, 1, 0.3, 1]
};

/** First page index of each logical blog in merged head+snake content. */
function getSortedBlogStartPageIndices(pages, snakeMetadata) {
  if (!pages || pages.length === 0) return [];
  const starts = new Set([0]);
  const headN =
    snakeMetadata != null && snakeMetadata.snakeStartPageIndex !== undefined
      ? snakeMetadata.snakeStartPageIndex
      : pages.length;
  if (headN > 0 && headN < pages.length) starts.add(headN);
  for (let i = headN + 1; i < pages.length; i++) {
    const prevMeta = Array.isArray(pages[i - 1]) ? null : pages[i - 1];
    const curMeta = Array.isArray(pages[i]) ? null : pages[i];
    const prevId = prevMeta?._sourceBlogId != null ? String(prevMeta._sourceBlogId) : '';
    const curId = curMeta?._sourceBlogId != null ? String(curMeta._sourceBlogId) : '';
    if (curId !== prevId) starts.add(i);
  }
  return Array.from(starts).sort((a, b) => a - b);
}

/** Next page for vertical swipe-up: first page of the next blog in order only (no +1 within a segment). */
function getNextBlogNavPage(currentPage, blogStarts) {
  const nextBlogStart = blogStarts.find((s) => s > currentPage);
  return nextBlogStart !== undefined ? nextBlogStart : null;
}

/** Previous blog’s first page (vertical swipe down). */
function getPreviousBlogNavPage(currentPage, blogStarts) {
  if (currentPage <= 0 || !blogStarts.length) return null;
  let myStart = 0;
  for (const s of blogStarts) {
    if (s <= currentPage) myStart = s;
    else break;
  }
  const idx = blogStarts.indexOf(myStart);
  if (idx <= 0) return null;
  return blogStarts[idx - 1];
}

/** Inclusive page range for one logical blog (head or one snake source). */
function getBlogSegmentBoundsForPage(pageIndex, blogStarts, totalPages) {
  const starts = blogStarts && blogStarts.length > 0 ? blogStarts : [0];
  let segmentStart = 0;
  for (const s of starts) {
    if (s <= pageIndex) segmentStart = s;
  }
  let segmentEnd = totalPages - 1;
  for (const s of starts) {
    if (s > pageIndex) {
      segmentEnd = s - 1;
      break;
    }
  }
  return { segmentStart, segmentEnd };
}

const PublicBlogPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentSheetOpen, setCommentSheetOpen] = useState(false);
  const [infoSheetOpen, setInfoSheetOpen] = useState(false);
  const siteConfig = useSiteConfig();
  const [snakeMetadata, setSnakeMetadata] = useState(null); // Store snake pagination metadata
  const [loadingMoreSnakePages, setLoadingMoreSnakePages] = useState(false);
  const [loadingNextSubcat, setLoadingNextSubcat] = useState(false);
  const [showNextBlogHint, setShowNextBlogHint] = useState(false);
  const [prebuiltBlogPageMapping, setPrebuiltBlogPageMapping] = useState(null); // Store pageAffiliateMapping from prebuilt blog

  // Track visited subcategories and their page positions for looping
  const [visitedSubcategories, setVisitedSubcategories] = useState([]); // Array of { subcategoryId, categoryId, prebuiltBlogId }
  const [subcategoryPagePositions, setSubcategoryPagePositions] = useState({}); // Map: subcategoryId -> page number
  const [isSnakeOnlyMode, setIsSnakeOnlyMode] = useState(false); // Track if we're in snake-only mode (no head blog)



  // Handle affiliate signup button click
  const handleAffiliateSignup = () => {
    window.open('/signup?role=affiliate', '_blank', 'noopener,noreferrer');
  };

  // Handle share button click - use native sharing sheet
  const handleShareClick = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blogPost?.title || 'Check out this blog post',
          text: blogPost?.title || 'Check out this blog post',
          url: window.location.href
        });
      } catch (error) {
        // User cancelled or error occurred - silently handle
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback: copy link to clipboard if native share is not available
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy link:', error);
        alert('Sharing is not available on this device.');
      }
    }
  };

  // Handle "Next topic" button click - show snake for next available subcategory
  const handleNextSubcategory = async () => {
    if (!blogPost || !blogPost.categoryId || !blogPost.subcategoryId || loadingNextSubcat) {
      console.log('⚠️ Cannot proceed:', {
        hasBlogPost: !!blogPost,
        categoryId: blogPost?.categoryId,
        subcategoryId: blogPost?.subcategoryId,
        loadingNextSubcat
      });
      return;
    }

    try {
      setLoadingNextSubcat(true);
      console.log('🔍 Looking for next subcategory snake...');
      console.log('📝 Current blog post:', {
        categoryId: blogPost.categoryId,
        subcategoryId: blogPost.subcategoryId,
        categoryIdType: typeof blogPost.categoryId,
        subcategoryIdType: typeof blogPost.subcategoryId
      });

      // Get category with all subcategories
      const categories = await apiService.getCategoriesForSelect();
      console.log('📁 All categories:', categories.map(c => ({ id: c._id, name: c.name, subcatCount: c.subcategories?.length || 0 })));

      // Normalize IDs for comparison (handle both string, ObjectId, and populated object formats)
      // categoryId might be a string, ObjectId, or populated object like { _id: '...', name: '...' }
      let currentCategoryId = blogPost.categoryId;
      if (currentCategoryId && typeof currentCategoryId === 'object' && currentCategoryId._id) {
        // It's a populated object, extract the _id
        currentCategoryId = currentCategoryId._id;
      }
      currentCategoryId = currentCategoryId?.toString() || currentCategoryId;

      console.log('🔍 Looking for category ID:', currentCategoryId);
      console.log('📝 blogPost.categoryId type:', typeof blogPost.categoryId, blogPost.categoryId);

      const currentCategory = categories.find(cat => {
        const catId = cat._id?.toString() || cat._id;
        const matches = catId === currentCategoryId || catId?.toString() === currentCategoryId?.toString();
        if (matches) {
          console.log(`✅ Category match found: ${cat.name} (${catId} === ${currentCategoryId})`);
        }
        return matches;
      });

      console.log('📁 Current category:', {
        found: !!currentCategory,
        categoryId: currentCategory?._id,
        name: currentCategory?.name,
        subcategories: currentCategory?.subcategories?.map(s => ({ id: s._id, name: s.name }))
      });

      // Debug: Log all categories and their subcategories
      console.log('📋 All categories with subcategories:');
      categories.forEach(cat => {
        console.log(`  - ${cat.name} (${cat._id}): ${cat.subcategories?.length || 0} subcategories`);
        if (cat.subcategories && cat.subcategories.length > 0) {
          cat.subcategories.forEach(sub => {
            console.log(`    • ${sub.name} (${sub._id})`);
          });
        }
      });

      if (!currentCategory) {
        console.error('❌ Category not found:', {
          searchedCategoryId: currentCategoryId,
          availableCategories: categories.map(c => ({ id: c._id, name: c.name }))
        });
        alert(`Category not found. Current category ID: ${currentCategoryId}`);
        return;
      }

      if (!currentCategory.subcategories || currentCategory.subcategories.length === 0) {
        console.error('❌ No subcategories found:', {
          hasCategory: !!currentCategory,
          categoryName: currentCategory?.name,
          subcategoriesCount: currentCategory?.subcategories?.length || 0,
          note: 'Note: Only active subcategories are returned by the API'
        });
        alert(`No active subcategories found in category "${currentCategory.name}". Note: Only active subcategories are shown.`);
        return;
      }

      // Find current subcategory index
      // subcategoryId might be a string, ObjectId, or populated object
      let currentSubcatId = blogPost.subcategoryId;
      if (currentSubcatId && typeof currentSubcatId === 'object' && currentSubcatId._id) {
        // It's a populated object, extract the _id
        currentSubcatId = currentSubcatId._id;
      }
      currentSubcatId = currentSubcatId?.toString() || currentSubcatId;

      console.log('🔍 Looking for current subcategory:', currentSubcatId);
      console.log('📝 blogPost.subcategoryId type:', typeof blogPost.subcategoryId, blogPost.subcategoryId);

      const currentIndex = currentCategory.subcategories.findIndex((subcat, idx) => {
        const subcatId = subcat._id?.toString() || subcat._id;
        const matches = subcatId === currentSubcatId;
        if (matches) {
          console.log(`✅ Found current subcategory at index ${idx}: ${subcat.name}`);
        }
        return matches;
      });

      console.log('📍 Current subcategory index:', currentIndex);
      console.log('📋 All subcategories in current category:', currentCategory.subcategories.map((s, i) => ({
        index: i,
        id: s._id,
        name: s.name,
        isCurrent: i === currentIndex,
        willBeChecked: i > currentIndex
      })));
      console.log(`📊 Subcategory search plan: Will check ${currentCategory.subcategories.length - currentIndex - 1} subcategories (indices ${currentIndex + 1} to ${currentCategory.subcategories.length - 1})`);

      if (currentIndex === -1) {
        console.error('❌ Current subcategory not found:', {
          currentSubcatId,
          availableSubcats: currentCategory.subcategories.map(s => s._id?.toString() || s._id)
        });
        alert(`Current subcategory not found. Available: ${currentCategory.subcategories.map(s => s.name).join(', ')}`);
        return;
      }

      // Find next subcategory with a prebuilt merged blog
      let nextSubcategory = null;
      let nextCategory = null;
      const checkedSubcategories = []; // Track all checked subcategories for summary
      console.log(`🔍 Searching for next subcategory starting from index ${currentIndex + 1}...`);
      console.log(`📋 Current subcategory: "${currentCategory.subcategories[currentIndex]?.name}" (index ${currentIndex})`);

      // First, verify we're not checking the current subcategory
      const currentSubcatIdNormalized = currentSubcatId;

      // Search remaining subcategories in current category
      // First, check subcategories AFTER the current one
      const remainingSubcatsInCategory = currentCategory.subcategories.length - currentIndex - 1;
      console.log(`🔍 Searching ${remainingSubcatsInCategory} remaining subcategories AFTER current one in "${currentCategory.name}" (starting from index ${currentIndex + 1})`);
      console.log(`📋 Subcategories that will be checked (after current):`, currentCategory.subcategories.slice(currentIndex + 1).map((s, idx) => ({
        relativeIndex: idx,
        absoluteIndex: currentIndex + 1 + idx,
        name: s.name,
        id: s._id
      })));

      for (let i = currentIndex + 1; i < currentCategory.subcategories.length; i++) {
        const subcat = currentCategory.subcategories[i];
        const subcatIdNormalized = subcat._id?.toString() || subcat._id;

        // Skip if this is the current subcategory (safety check)
        if (subcatIdNormalized === currentSubcatIdNormalized) {
          console.log(`  ⏭️  Skipping current subcategory "${subcat.name}" (index ${i})`);
          continue;
        }

        console.log(`  📄 Checking subcategory ${i + 1}/${currentCategory.subcategories.length}: "${subcat.name}" (ID: ${subcatIdNormalized})`);

        try {
          const prebuiltBlog = await apiService.getPreBuiltMergedBlogBySubcategory(
            currentCategory._id,
            subcat._id
          );
          console.log(`  ✅ Found prebuilt blog for "${subcat.name}":`, {
            id: prebuiltBlog._id,
            published: prebuiltBlog.published,
            hasContent: !!prebuiltBlog.content,
            categoryId: prebuiltBlog.categoryId,
            subcategoryId: prebuiltBlog.subcategoryId
          });

          checkedSubcategories.push({
            category: currentCategory.name,
            subcategory: subcat.name,
            status: prebuiltBlog.published ? 'published' : 'unpublished',
            hasBlog: true
          });

          if (prebuiltBlog && prebuiltBlog.published) {
            nextSubcategory = subcat;
            nextCategory = currentCategory;
            console.log(`✅ Found next subcategory with snake: ${subcat.name} (index ${i})`);
            break;
          } else if (prebuiltBlog && !prebuiltBlog.published) {
            console.log(`  ⏭️  Subcategory "${subcat.name}" has prebuilt blog but it's not published, skipping...`);
          }
        } catch (error) {
          // Subcategory doesn't have a prebuilt merged blog, continue searching
          const errorStatus = error.response?.status;
          const errorMessage = error.response?.data?.message || error.message;
          console.log(`  ⏭️  Subcategory "${subcat.name}" has no snake:`, {
            status: errorStatus,
            message: errorMessage,
            categoryId: currentCategory._id,
            subcategoryId: subcat._id
          });

          checkedSubcategories.push({
            category: currentCategory.name,
            subcategory: subcat.name,
            status: 'no_prebuilt_blog',
            hasBlog: false,
            error: errorStatus === 404 ? 'Not found' : errorMessage
          });
        }
      }

      // If not found in remaining subcategories, check earlier subcategories in the same category
      // (This handles the case where current subcategory is the last one)
      if (!nextSubcategory && currentIndex > 0) {
        console.log(`🔄 No published prebuilt blogs found in remaining subcategories, checking earlier subcategories in "${currentCategory.name}"...`);
        console.log(`📋 Subcategories that will be checked (before current):`, currentCategory.subcategories.slice(0, currentIndex).map((s, idx) => ({
          index: idx,
          name: s.name,
          id: s._id
        })));

        for (let i = 0; i < currentIndex; i++) {
          const subcat = currentCategory.subcategories[i];
          const subcatIdNormalized = subcat._id?.toString() || subcat._id;

          console.log(`  📄 Checking earlier subcategory ${i + 1}/${currentIndex}: "${subcat.name}" (ID: ${subcatIdNormalized})`);

          try {
            const prebuiltBlog = await apiService.getPreBuiltMergedBlogBySubcategory(
              currentCategory._id,
              subcat._id
            );
            console.log(`  ✅ Found prebuilt blog for "${subcat.name}":`, {
              id: prebuiltBlog._id,
              published: prebuiltBlog.published,
              hasContent: !!prebuiltBlog.content,
              categoryId: prebuiltBlog.categoryId,
              subcategoryId: prebuiltBlog.subcategoryId
            });

            checkedSubcategories.push({
              category: currentCategory.name,
              subcategory: subcat.name,
              status: prebuiltBlog.published ? 'published' : 'unpublished',
              hasBlog: true
            });

            if (prebuiltBlog && prebuiltBlog.published) {
              nextSubcategory = subcat;
              nextCategory = currentCategory;
              console.log(`✅ Found next subcategory with snake in same category: ${subcat.name} (index ${i})`);
              break;
            } else if (prebuiltBlog && !prebuiltBlog.published) {
              console.log(`  ⏭️  Subcategory "${subcat.name}" has prebuilt blog but it's not published, skipping...`);
            }
          } catch (error) {
            const errorStatus = error.response?.status;
            const errorMessage = error.response?.data?.message || error.message;
            console.log(`  ⏭️  Subcategory "${subcat.name}" has no snake:`, {
              status: errorStatus,
              message: errorMessage,
              categoryId: currentCategory._id,
              subcategoryId: subcat._id
            });

            checkedSubcategories.push({
              category: currentCategory.name,
              subcategory: subcat.name,
              status: 'no_prebuilt_blog',
              hasBlog: false,
              error: errorStatus === 404 ? 'Not found' : errorMessage
            });
          }
        }
      }

      // If still not found, move to next category and start from first subcategory
      if (!nextSubcategory) {
        console.log(`🔄 No published prebuilt blogs found in "${currentCategory.name}" category, moving to next category...`);

        const currentCategoryIndex = categories.findIndex(cat => {
          const catId = cat._id?.toString() || cat._id;
          return catId === currentCategoryId;
        });

        console.log(`📍 Current category index: ${currentCategoryIndex}`);

        if (currentCategoryIndex === -1) {
          console.error('❌ Current category not found in categories list:', {
            searchedCategoryId: currentCategoryId,
            availableCategories: categories.map(c => ({ id: c._id?.toString(), name: c.name }))
          });
          alert(`Current category not found. This might happen if the category was deactivated.`);
          return;
        }

        // Search next categories
        const startCategoryIndex = currentCategoryIndex + 1;
        const endCategoryIndex = categories.length - 1;
        const remainingCategories = categories.length - startCategoryIndex;
        console.log(`🔍 Starting search from category index ${startCategoryIndex} to ${endCategoryIndex} (${remainingCategories} remaining category/categories)`);

        if (remainingCategories === 0) {
          console.log(`⚠️  No more categories to search (current category "${currentCategory.name}" is the last one)`);
        }

        for (let catIdx = startCategoryIndex; catIdx < categories.length; catIdx++) {
          const category = categories[catIdx];
          console.log(`  📁 Checking category ${catIdx}/${categories.length - 1}: "${category.name}" (${category.subcategories?.length || 0} subcategories)`);

          if (!category.subcategories || category.subcategories.length === 0) {
            console.log(`  ⏭️  Category "${category.name}" has no subcategories, skipping...`);
            continue;
          }

          // Search all subcategories in this category
          console.log(`  🔍 Checking ${category.subcategories.length} subcategories in "${category.name}"...`);
          for (let subIdx = 0; subIdx < category.subcategories.length; subIdx++) {
            const subcat = category.subcategories[subIdx];
            console.log(`    📄 Checking subcategory ${subIdx + 1}/${category.subcategories.length}: "${subcat.name}"`);

            try {
              const prebuiltBlog = await apiService.getPreBuiltMergedBlogBySubcategory(
                category._id,
                subcat._id
              );
              console.log(`    ✅ Found prebuilt blog for "${subcat.name}":`, {
                id: prebuiltBlog._id,
                published: prebuiltBlog.published,
                categoryId: prebuiltBlog.categoryId,
                subcategoryId: prebuiltBlog.subcategoryId
              });

              checkedSubcategories.push({
                category: category.name,
                subcategory: subcat.name,
                status: prebuiltBlog.published ? 'published' : 'unpublished',
                hasBlog: true
              });

              if (prebuiltBlog && prebuiltBlog.published) {
                nextSubcategory = subcat;
                nextCategory = category;
                console.log(`✅ Found next subcategory with snake in next category: ${category.name} > ${subcat.name}`);
                break; // Break out of subcategory loop
              } else if (prebuiltBlog && !prebuiltBlog.published) {
                console.log(`    ⏭️  Subcategory "${subcat.name}" has prebuilt blog but it's not published, skipping...`);
              }
            } catch (error) {
              // Subcategory doesn't have a prebuilt merged blog, continue searching
              const errorStatus = error.response?.status;
              const errorMessage = error.response?.data?.message || error.message;
              console.log(`    ⏭️  Subcategory "${subcat.name}" has no snake:`, {
                status: errorStatus,
                message: errorMessage,
                categoryId: category._id,
                subcategoryId: subcat._id
              });

              checkedSubcategories.push({
                category: category.name,
                subcategory: subcat.name,
                status: 'no_prebuilt_blog',
                hasBlog: false,
                error: errorStatus === 404 ? 'Not found' : errorMessage
              });
            }
          }

          // If we found a subcategory with a published blog, stop searching categories
          if (nextSubcategory) {
            console.log(`✅ Found published prebuilt blog, stopping search`);
            break; // Found one, stop searching categories
          } else {
            const isLastCategory = catIdx === categories.length - 1;
            if (isLastCategory) {
              console.log(`  ❌ No published prebuilt blogs found in "${category.name}" (last category, search complete)`);
            } else {
              console.log(`  ❌ No published prebuilt blogs found in "${category.name}", continuing to next category...`);
            }
          }
        }

        // Log if we've exhausted all categories
        if (!nextSubcategory && remainingCategories > 0) {
          console.log(`🔚 Finished searching all ${remainingCategories} remaining category/categories`);
        }
      } else {
        // Found in current category, use current category
        nextCategory = currentCategory;
      }

      // If no next subcategory found, check if we should loop back to first visited subcategory
      if (!nextSubcategory || !nextCategory) {
        // If we have visited subcategories, loop back to the first one
        if (visitedSubcategories.length > 0) {
          console.log(`🔄 No more subcategories found, looping back to first visited subcategory`);
          const firstVisited = visitedSubcategories[0];

          // Get the prebuilt blog for the first visited subcategory
          try {
            const loopPrebuiltBlog = await apiService.getPreBuiltMergedBlogBySubcategory(
              firstVisited.categoryId,
              firstVisited.subcategoryId
            );

            if (loopPrebuiltBlog && loopPrebuiltBlog.published) {
              // Use the first visited subcategory as next
              const loopCategory = categories.find(cat => {
                const catId = cat._id?.toString() || cat._id;
                return catId === firstVisited.categoryId;
              });
              const loopSubcategory = loopCategory?.subcategories?.find(sub => {
                const subId = sub._id?.toString() || sub._id;
                return subId === firstVisited.subcategoryId;
              });

              if (loopCategory && loopSubcategory) {
                nextSubcategory = loopSubcategory;
                nextCategory = loopCategory;
                console.log(`✅ Looping back to first visited subcategory: ${loopSubcategory.name}`);
              }
            }
          } catch (error) {
            console.error('Error loading loop subcategory:', error);
          }
        }

        // If still no next subcategory, show error
        if (!nextSubcategory || !nextCategory) {
          console.error('❌ No subcategories with published prebuilt blogs found');
          console.log('📊 Summary of checked subcategories:');
          checkedSubcategories.forEach((item, idx) => {
            console.log(`  ${idx + 1}. ${item.category} > ${item.subcategory}: ${item.status}${item.error ? ` (${item.error})` : ''}`);
          });
          console.log('📈 Statistics:', {
            totalChecked: checkedSubcategories.length,
            withPublishedBlogs: checkedSubcategories.filter(s => s.status === 'published').length,
            withUnpublishedBlogs: checkedSubcategories.filter(s => s.status === 'unpublished').length,
            withoutBlogs: checkedSubcategories.filter(s => s.status === 'no_prebuilt_blog').length
          });

          const unpublishedBlogs = checkedSubcategories.filter(s => s.status === 'unpublished');
          if (unpublishedBlogs.length > 0) {
            alert(`No published prebuilt blogs found. Found ${unpublishedBlogs.length} unpublished prebuilt blog(s): ${unpublishedBlogs.map(s => s.subcategory).join(', ')}. Please publish them in the admin portal.`);
          } else {
            alert(`No published prebuilt blogs (snakes) found in any subcategory. Checked ${checkedSubcategories.length} subcategories. Make sure you have created and published a prebuilt merged blog for another subcategory.`);
          }
          return;
        }
      }

      // Verify we're not selecting the same subcategory
      const nextSubcatIdNormalized = nextSubcategory._id?.toString() || nextSubcategory._id;
      if (nextSubcatIdNormalized === currentSubcatIdNormalized) {
        console.error('❌ Error: Selected subcategory is the same as current subcategory!');
        alert('Error: Next subcategory is the same as current. Please check console for details.');
        return;
      }

      // Update the blog to attach the next subcategory's snake
      console.log(`🔄 Updating blog to attach snake from category: ${nextCategory.name} > subcategory: ${nextSubcategory.name}`);
      console.log(`📊 Comparison: Current="${currentSubcatIdNormalized}" vs Next="${nextSubcatIdNormalized}"`);

      // Get the prebuilt merged blog for the next subcategory
      const nextPrebuiltBlog = await apiService.getPreBuiltMergedBlogBySubcategory(
        nextCategory._id,
        nextSubcategory._id
      );

      if (!nextPrebuiltBlog || !nextPrebuiltBlog.published) {
        alert('Prebuilt merged blog not found for next subcategory');
        return;
      }

      // Replace snake with new subcategory's snake, but keep the head blog pages
      // Parse current blog content to get head blog pages
      let allCurrentPages = [];
      try {
        const blogContent = typeof blogPost.content === 'string' ? JSON.parse(blogPost.content) : blogPost.content;
        allCurrentPages = Array.isArray(blogContent) ? blogContent : [];
      } catch (e) {
        console.error('Error parsing blog content:', e);
      }

      // Determine where the snake starts (head blog ends)
      // If we have snake metadata, use snakeStartPageIndex, otherwise assume all pages are head blog
      const snakeStartIndex = snakeMetadata?.snakeStartPageIndex ?? allCurrentPages.length;
      const headBlogPages = allCurrentPages.slice(0, snakeStartIndex);

      console.log(`📊 Current structure: ${headBlogPages.length} head pages + ${allCurrentPages.length - snakeStartIndex} snake pages`);
      console.log(`🔄 Replacing snake, keeping ${headBlogPages.length} head pages`);

      // Parse new prebuilt blog content (snake from next subcategory)
      let prebuiltPages = [];
      try {
        const prebuiltContent = typeof nextPrebuiltBlog.content === 'string'
          ? JSON.parse(nextPrebuiltBlog.content)
          : nextPrebuiltBlog.content;
        prebuiltPages = Array.isArray(prebuiltContent) ? prebuiltContent : [];
      } catch (e) {
        console.error('Error parsing prebuilt blog content:', e);
      }

      // Check if we're switching to a previously visited subcategory
      const newSubcategoryId = nextSubcategory._id?.toString() || nextSubcategory._id;
      const newCategoryId = nextCategory._id?.toString() || nextCategory._id;
      const isRevisiting = visitedSubcategories.some(v => v.subcategoryId === newSubcategoryId);

      // Save current page position before switching (if we have a current subcategory)
      // Use functional update to ensure we get the latest state and can use it immediately
      let updatedPagePositions = { ...subcategoryPagePositions };
      if (blogPost.subcategoryId && currentPage >= 0) {
        const currentSubcatId = blogPost.subcategoryId?.toString() || blogPost.subcategoryId;
        updatedPagePositions[currentSubcatId] = currentPage;
        setSubcategoryPagePositions(updatedPagePositions);
        console.log(`💾 Saved page position ${currentPage} for subcategory ${currentSubcatId}`);
      }

      // Fetch pages dynamically from API (supports dynamic generation from sourceBlogIds)
      // Use the updated positions map to get the saved position (handles async state updates)
      const savedPagePosition = isRevisiting ? (updatedPagePositions[newSubcategoryId] || subcategoryPagePositions[newSubcategoryId] || 0) : 0;

      console.log(`📍 Page position lookup:`, {
        isRevisiting,
        newSubcategoryId,
        savedPagePosition,
        updatedPositions: updatedPagePositions,
        statePositions: subcategoryPagePositions
      });
      const SNAKE_INITIAL_PAGE_COUNT = 10;

      // If revisiting and we have a saved position > 0, load pages from 0 to savedPosition + buffer
      // Otherwise, load first 10 pages
      const startIndex = 0;
      const limit = (isRevisiting && savedPagePosition > 0)
        ? Math.min(savedPagePosition + SNAKE_INITIAL_PAGE_COUNT, 100) // Load up to saved position + buffer
        : SNAKE_INITIAL_PAGE_COUNT;

      console.log(`📥 Fetching snake pages dynamically: start=${startIndex}, limit=${limit}, savedPosition=${savedPagePosition}`);

      const affiliateId = blogPost.author?._id || blogPost.author;
      const snakePagesResponse = await apiService.getSnakePages(
        nextPrebuiltBlog._id.toString(),
        startIndex,
        limit,
        affiliateId,
        newSubcategoryId
      );

      const fetchedPages = snakePagesResponse.pages || [];
      const totalSnakePages = snakePagesResponse.total || fetchedPages.length;

      console.log(`✅ Fetched ${fetchedPages.length} snake pages (total available: ${totalSnakePages})`);

      // Replace snake: show ONLY snake pages (no head blog) when switching subcategories
      if (fetchedPages.length > 0) {
        const initialSnakePages = fetchedPages;

        // Start with ONLY snake pages (no head blog)
        const combinedPages = [];
        let nextPageId = 1;

        // Add snake pages starting from saved position
        initialSnakePages.forEach((page, idx) => {
          combinedPages.push({
            ...page,
            id: nextPageId++
          });
        });

        // Update blog post state with snake-only content
        console.log('🔄 Updating blogPost state with new category/subcategory (snake-only mode):', {
          categoryId: newCategoryId,
          subcategoryId: newSubcategoryId,
          categoryName: nextCategory.name,
          subcategoryName: nextSubcategory.name,
          isRevisiting,
          savedPagePosition,
          startIndex,
          initialPagesLoaded: initialSnakePages.length
        });

        setBlogPost(prev => ({
          ...prev,
          content: JSON.stringify(combinedPages),
          categoryId: newCategoryId,
          subcategoryId: newSubcategoryId,
          _hasAttachedPrebuiltBlog: true,
          _snakeMetadata: {
            prebuiltBlogId: nextPrebuiltBlog._id.toString(),
            totalSnakePages: totalSnakePages,
            loadedSnakePages: initialSnakePages.length,
            loadedStartIndex: startIndex, // Track where we started loading
            snakeStartPageIndex: 0, // No head blog in snake-only mode
            hasMoreSnakePages: snakePagesResponse.hasMore !== false && (startIndex + initialSnakePages.length < totalSnakePages)
          },
          _fetchedAt: Date.now()
        }));

        // Store snake metadata
        setSnakeMetadata({
          prebuiltBlogId: nextPrebuiltBlog._id.toString(),
          totalSnakePages: totalSnakePages,
          loadedSnakePages: initialSnakePages.length,
          loadedStartIndex: startIndex,
          snakeStartPageIndex: 0, // No head blog
          hasMoreSnakePages: snakePagesResponse.hasMore !== false && (startIndex + initialSnakePages.length < totalSnakePages)
        });

        // Store prebuilt blog's pageAffiliateMapping for correct affiliate tracking
        if (nextPrebuiltBlog.pageAffiliateMapping) {
          setPrebuiltBlogPageMapping(nextPrebuiltBlog.pageAffiliateMapping);
          console.log('📊 Loaded new prebuilt blog pageAffiliateMapping:', nextPrebuiltBlog.pageAffiliateMapping);
        }

        // Track visited subcategories
        if (!isRevisiting) {
          setVisitedSubcategories(prev => {
            const exists = prev.some(v => v.subcategoryId === newSubcategoryId);
            if (!exists) {
              return [...prev, {
                subcategoryId: newSubcategoryId,
                categoryId: newCategoryId,
                prebuiltBlogId: nextPrebuiltBlog._id.toString()
              }];
            }
            return prev;
          });
        }

        // Set snake-only mode
        setIsSnakeOnlyMode(true);

        // Restore saved page position or start from beginning
        // Ensure the page position is within bounds of loaded pages
        const targetPage = Math.min(Math.max(0, savedPagePosition), initialSnakePages.length - 1);
        console.log(`📍 Restoring page position:`, {
          savedPosition: savedPagePosition,
          loadedPages: initialSnakePages.length,
          targetPage: targetPage,
          isRevisiting: isRevisiting,
          subcategoryId: newSubcategoryId
        });

        // Set page position after content is updated
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (targetPage !== currentPageRef.current) {
              skipNextPageTrackRef.current = true; // programmatic jump — loadBlogPost handles tracking
              setCurrentPage(targetPage);
            }
            console.log(`✅ Set currentPage to ${targetPage} for subcategory ${nextSubcategory.name} (revisit: ${isRevisiting})`);
          }, 200);
        });

        console.log(`✅ Successfully switched to snake-only mode from subcategory: ${nextSubcategory.name} (${initialSnakePages.length}/${totalSnakePages} snake pages, will restore to page ${targetPage})`);
      } else {
        alert('Prebuilt merged blog has no pages');
      }
    } catch (error) {
      console.error('❌ Error loading next subcategory snake:', error);
      alert('Failed to load next subcategory snake: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingNextSubcat(false);
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useReducedMotion();

  // Mobile pagination state
  const [currentPage, setCurrentPage] = useState(0);

  // Show 'next blog' hint: appear after 1s, stay for 2s, then disappear — resets on every page
  useEffect(() => {
    setShowNextBlogHint(false); // Reset immediately on page change
    const showTimer = setTimeout(() => setShowNextBlogHint(true), 1000);
    const hideTimer = setTimeout(() => setShowNextBlogHint(false), 3000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [currentPage]);
  /** 'y' = translateY (swipe up/down); 'x' = translateX (swipe left/right + side page buttons) */
  const [pageSlideAxis, setPageSlideAxis] = useState('y');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);
  const trackingAbortControllerRef = useRef(null);
  const hasTrackedRef = useRef(false);
  const skipNextPageTrackRef = useRef(false); // True when page change is programmatic (resume/URL/subcategory) — skip view tracking
  const lastSwipeTrackRef = useRef({}); // Map of pageNumber -> timestamp for rapid-fire cooldown (per-page, 1s)
  const touchStartRef = useRef(null); // Use ref to avoid closure issues (Y axis)
  const touchEndRef = useRef(null); // Use ref to avoid closure issues (Y axis)
  const touchStartXRef = useRef(null); // X axis start for horizontal swipes
  const touchEndXRef = useRef(null); // X axis end for horizontal swipes
  const currentPageRef = useRef(0); // Use ref to track current page for swipe handler
  const prevPageRef = useRef(0);    // Track previous page so the animation only shows entry/exit pages
  const isLoadingFromUrlRef = useRef(false); // Track if we're loading page from URL

  // Refs to hold latest state for use inside effects without causing re-fires
  // This prevents the page tracking effect from re-running when blogPost content
  // changes (e.g. snake pages appended) or when prebuiltBlogPageMapping resolves.
  const blogPostRef = useRef(null);
  const snakeMetadataRef = useRef(null);
  const prebuiltBlogPageMappingRef = useRef(null);

  useEffect(() => {
    // Create a unique key for this effect run (to detect StrictMode double runs)
    const effectKey = `${slug}-${Date.now()}-${Math.random()}`;

    // Reset tracking flag when slug changes
    if (hasTrackedRef.current === slug) {
      // Already tracked for this slug, skip
      console.log('⚠️  [EFFECT] Already tracked for this slug, skipping effect:', slug);
      return;
    }

    // Abort any pending tracking requests
    if (trackingAbortControllerRef.current) {
      trackingAbortControllerRef.current.abort();
    }

    const loadBlogPost = async () => {
      if (!slug) {
        setError('No blog slug provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('📄 [EFFECT] Loading public blog post with slug:', slug, 'effectKey:', effectKey);

        // You'll need to create this API endpoint
        const response = await apiService.getPublicBlogPost(slug);
        console.log('📄 [EFFECT] Public blog post response:', response);

        if (response && response.blog) {
          // Log content info for debugging
          const contentLength = response.blog.content?.length || 0;
          const contentPreview = response.blog.content?.substring(0, 100) || '';
          console.log(`📄 [EFFECT] Blog content length: ${contentLength}, preview: ${contentPreview}...`);
          console.log(`📄 [EFFECT] Blog has attached prebuilt merged blog: ${response.blog.attachedPrebuiltMergedBlogId || 'none'}`);
          console.log(`📄 [EFFECT] Blog has dynamic content flag: ${response.blog._hasAttachedPrebuiltBlog || false}`);

          // Parse and count pages for debugging
          try {
            const parsedContent = JSON.parse(response.blog.content);
            if (Array.isArray(parsedContent)) {
              console.log(`📄 [EFFECT] Blog has ${parsedContent.length} pages in content`);
              console.log(`📄 [EFFECT] Page IDs:`, parsedContent.map(p => p.id || 'no-id').join(', '));
            }
          } catch (e) {
            console.warn('📄 [EFFECT] Could not parse content for page count:', e);
          }

          // Store snake metadata if present
          let fetchedPrebuiltMapping = null; // Store fetched mapping for use in tracking
          if (response.blog._snakeMetadata) {
            setSnakeMetadata(response.blog._snakeMetadata);
            console.log('🐍 Snake metadata:', response.blog._snakeMetadata);

            // If blog has attached prebuilt merged blog, fetch its pageAffiliateMapping
            if (response.blog.attachedPrebuiltMergedBlogId) {
              try {
                const categoryId = response.blog.categoryId?._id || response.blog.categoryId;
                const subcategoryId = response.blog.subcategoryId?._id || response.blog.subcategoryId;
                if (categoryId && subcategoryId) {
                  const prebuiltBlog = await apiService.getPreBuiltMergedBlogBySubcategory(
                    categoryId,
                    subcategoryId
                  );
                  if (prebuiltBlog && prebuiltBlog.pageAffiliateMapping) {
                    fetchedPrebuiltMapping = prebuiltBlog.pageAffiliateMapping; // Store for tracking
                    setPrebuiltBlogPageMapping(prebuiltBlog.pageAffiliateMapping);
                    console.log('📊 Loaded prebuilt blog pageAffiliateMapping:', prebuiltBlog.pageAffiliateMapping);
                  }
                }
              } catch (error) {
                console.warn('Could not fetch prebuilt blog pageAffiliateMapping:', error);
              }
            }
          }

          // Also check if blog has pageAffiliateMapping directly (for merged blogs)
          if (response.blog.pageAffiliateMapping) {
            // Use blog's own mapping if prebuilt wasn't fetched
            if (!fetchedPrebuiltMapping) {
              fetchedPrebuiltMapping = response.blog.pageAffiliateMapping;
            }
            setPrebuiltBlogPageMapping(response.blog.pageAffiliateMapping);
            console.log('📊 Blog has pageAffiliateMapping:', response.blog.pageAffiliateMapping);
          }

          // Force state update by creating a new object reference
          // This ensures React detects the change even if content string is similar
          setBlogPost({
            ...response.blog,
            _fetchedAt: Date.now() // Add timestamp to force re-render
          });

          // Track page view after blog is loaded (only once per blog per session)
          if (response.blog.author && response.blog._id) {
            const blogPostId = response.blog._id;

            // CRITICAL: Check ref FIRST (synchronously) to prevent double calls from StrictMode
            if (hasTrackedRef.current === slug) {
              console.log('⚠️  [REF CHECK] Already tracked this slug in this render cycle, skipping:', slug, blogPostId);
              return;
            }

            // Check if we've already tracked this blog post (module-level singleton check)
            if (sessionTracking.isTracked(blogPostId)) {
              console.log('⚠️  [SINGLETON CHECK] Blog post already tracked in this session, skipping:', blogPostId);
              hasTrackedRef.current = slug; // Mark ref to prevent future calls
              return;
            }

            // Double-check if tracking is in progress
            if (sessionTracking.isInProgress(blogPostId)) {
              console.log('⚠️  [IN PROGRESS CHECK] Tracking already in progress for this blog, skipping');
              hasTrackedRef.current = slug; // Mark ref to prevent future calls
              return;
            }

            // Mark ref IMMEDIATELY (synchronous) to prevent double calls - use slug as value
            hasTrackedRef.current = slug;

            // Mark as in progress immediately (before async call)
            sessionTracking.markInProgress(blogPostId);
            sessionTracking.markAsTracked(blogPostId); // Mark as tracked immediately

            // Determine target page for initial tracking
            let targetPage = 0;
            const pageParam = searchParams.get('page');

            let parsedContentForTracking = [];
            try {
              parsedContentForTracking = JSON.parse(response.blog.content);
            } catch (e) { }

            let numPages = 0;
            if (Array.isArray(parsedContentForTracking)) {
              if (parsedContentForTracking.length > 0 && parsedContentForTracking[0].id && parsedContentForTracking[0].elements) {
                numPages = parsedContentForTracking.length;
              } else {
                numPages = createMobilePages(parsedContentForTracking).length;
              }
            }

            if (pageParam) {
              const p = parseInt(pageParam, 10);
              if (!isNaN(p) && p >= 0 && (numPages > 0 ? p < numPages : true)) {
                targetPage = p;
                console.log(`📄 [INITIAL TRACK] Using URL parameter page: ${targetPage}`);
              }
            } else {
              try {
                const lpResult = await apiService.getBlogLastPage(blogPostId);
                if (lpResult.success && lpResult.lastPageNumber > 0) {
                  const lp = lpResult.lastPageNumber;
                  if (lp >= 0 && (numPages > 0 ? lp < numPages : true)) {
                    targetPage = lp;
                    console.log(`📄 [INITIAL TRACK] Using backend last page: ${targetPage}`);
                  }
                }
              } catch (e) {
                console.error('Error fetching last page for initial tracking:', e);
              }
            }


            // Helper function to get correct affiliate for a page
            const getAffiliateForPage = (blogPost, pageNumber, snakeMeta, prebuiltMapping) => {
              // 1. Prioritize dynamically attached _sourceBlogAuthor from content pages
              try {
                const parsedContent = typeof blogPost.content === 'string' ? JSON.parse(blogPost.content) : blogPost.content;
                if (Array.isArray(parsedContent) && parsedContent.length > pageNumber) {
                  const targetPage = parsedContent[pageNumber];
                  if (targetPage && targetPage._sourceBlogAuthor) {
                    console.log(`  📍 Using _sourceBlogAuthor for page ${pageNumber}: ${targetPage._sourceBlogAuthor}`);
                    return targetPage._sourceBlogAuthor;
                  }
                }
              } catch (error) {
                console.warn('Error parsing content for affiliate check:', error);
              }

              // 2. Check if blog has pageAffiliateMapping directly (for merged blogs)
              if (blogPost.pageAffiliateMapping && typeof blogPost.pageAffiliateMapping === 'object') {
                const pageKey = pageNumber.toString();
                if (blogPost.pageAffiliateMapping[pageKey]) {
                  const mappedAffiliateId = blogPost.pageAffiliateMapping[pageKey];
                  console.log(`  📍 Using pageAffiliateMapping for page ${pageNumber}: ${mappedAffiliateId}`);
                  return mappedAffiliateId;
                }
              }

              // 3. Check prebuilt blog's pageAffiliateMapping (for dynamically attached snakes)
              if (prebuiltMapping && typeof prebuiltMapping === 'object' && snakeMeta) {
                // For snake pages, adjust page number to snake's index
                // Also handle snake-only mode where snakeStartPageIndex is 0
                const snakeStartIndex = snakeMeta.snakeStartPageIndex || 0;
                if (pageNumber >= snakeStartIndex) {
                  const snakePageIndex = pageNumber - snakeStartIndex;
                  const pageKey = snakePageIndex.toString();
                  if (prebuiltMapping[pageKey]) {
                    const mappedAffiliateId = prebuiltMapping[pageKey];
                    console.log(`  📍 Using prebuilt blog pageAffiliateMapping for snake page ${pageNumber} (snake index ${snakePageIndex}): ${mappedAffiliateId}`);
                    return mappedAffiliateId;
                  }
                }
              }

              // 4. Fallback: use head blog's affiliate (blog author)
              const authorId = blogPost.author?._id || blogPost.author;
              console.log(`  📍 Using blog author for page ${pageNumber}: ${authorId}`);
              return authorId;
            };

            // Use fetched prebuilt mapping if available, otherwise use blog's own mapping
            const mappingToUse = fetchedPrebuiltMapping || response.blog.pageAffiliateMapping;
            const affiliateId = getAffiliateForPage(
              response.blog,
              targetPage, // targetPage = initial load
              response.blog._snakeMetadata,
              mappingToUse // Use the fetched/available mapping
            );

            // Check if targetPage (initial load) has an offer element
            let offerId = null;
            let targetPageElements = [];
            let pageId = null;
            let offerElement = null;
            let sourceBlogId = null;
            try {

              if (Array.isArray(parsedContentForTracking) && parsedContentForTracking.length > 0 && parsedContentForTracking[0].id && parsedContentForTracking[0].elements) {
                // New format: pages structure - check target page
                targetPageElements = parsedContentForTracking[targetPage]?.elements || [];
                pageId = parsedContentForTracking[targetPage]?.id || parsedContentForTracking[targetPage]?._id || null;
                sourceBlogId = parsedContentForTracking[targetPage]?._sourceBlogId || null;
              } else if (Array.isArray(parsedContentForTracking)) {
                // Old format: flat elements array - use target page from createMobilePages
                const allPages = createMobilePages(parsedContentForTracking);
                targetPageElements = allPages[targetPage] || [];
              }

              // Find offer element in targetPage and check if it contains affiliate link
              offerElement = targetPageElements.find(el => el.type === 'offer');
              if (offerElement && hasAffiliateLinkInOfferElement(offerElement)) {
                offerId = offerElement.offerId || response.blog.offerId?._id || response.blog.offerId;
              }
            } catch (error) {
              console.error('Error parsing blog content for offer check:', error);
              // Fallback to blog-level offerId if parsing fails
              offerId = response.blog.offerId?._id || response.blog.offerId;
            }

            const sessionId = sessionTracking.getSessionId();

            console.log('📊 [TRACKING] Starting page view tracking...');
            console.log('  📝 Blog Post ID:', blogPostId);
            console.log('  👤 Affiliate ID:', affiliateId);
            console.log('  🎯 Offer ID:', offerId || 'N/A');
            console.log('  🆔 Session ID:', sessionId);
            console.log('  📋 Already tracked blogs:', Array.from(sessionTracking.trackedBlogIds));
            console.log('  🔒 Has tracked ref:', hasTrackedRef.current);
            console.log('  🔑 Effect key:', effectKey);

            // Track page view (don't await - fire and forget)
            // Initial blog load tracks the targetPage the user landed on
            apiService.trackBlogPageView(
              blogPostId,
              affiliateId,
              offerId,
              sessionId,
              targetPage,
              sourceBlogId,
              offerElement?.id || null, // offerElementId
              pageId
            ).then(result => {
              console.log(`✅ Blog page view tracked (initial load for page ${targetPage}):`, result);
              // tracking effect skips initial view
              sessionTracking.markComplete(blogPostId);
            }).catch(error => {
              console.error('❌ Error tracking blog page view:', error);
              sessionTracking.markComplete(blogPostId); // Remove from in-progress
              sessionTracking.trackedBlogIds.delete(blogPostId); // Remove from tracked set on error
              // Only reset ref if slug matches (prevent race conditions)
              if (hasTrackedRef.current === slug) {
                hasTrackedRef.current = false; // Reset ref on error so it can retry
              }
              // Don't show error to user, just log it
            });
          }
        } else {
          setError('Blog post not found');
        }
      } catch (error) {
        console.error('Error loading public blog post:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          config: error.config
        });

        // Provide more specific error messages
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          setError('Network error: Unable to connect to server. Please check your connection and try again.');
        } else if (error.response?.status === 404) {
          setError('Blog post not found');
        } else {
          setError('Failed to load blog post: ' + (error.message || 'Unknown error'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadBlogPost();

    // Cleanup function - don't reset ref here, only on slug change
    return () => {
      // Only reset if slug changed
      if (hasTrackedRef.current === slug) {
        // Don't reset - keep it to prevent duplicate tracking
        console.log('⚠️  [CLEANUP] Effect cleanup for slug:', slug);
      }
    };
  }, [slug]);

  // Keep refs in sync with their state counterparts
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    setPageSlideAxis('y');
  }, [slug]);

  useEffect(() => {
    blogPostRef.current = blogPost;
  }, [blogPost]);

  useEffect(() => {
    snakeMetadataRef.current = snakeMetadata;
  }, [snakeMetadata]);

  useEffect(() => {
    prebuiltBlogPageMappingRef.current = prebuiltBlogPageMapping;
  }, [prebuiltBlogPageMapping]);

  // Pause all videos when navigating to a different page
  useEffect(() => {
    // Find all video elements and pause them
    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      if (!video.paused) {
        console.log('⏸️ Pausing video on page change');
        video.pause();
      }
    });
  }, [currentPage]);

  // Block the browser's native scroll during a swipe gesture.
  //
  // React registers touch event listeners as "passive" on modern iOS/Android,
  // which means calling e.preventDefault() inside React handlers has no effect
  // — the browser scrolls the page anyway, competing with Framer Motion.
  //
  // We fix this by attaching a direct DOM listener with { passive: false }, which
  // lets us call e.preventDefault() for real and hand full control of movement
  // to Framer Motion while a swipe is in progress.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isMobile) return;

    const blockScroll = (e) => {
      // Only suppress native scroll when a swipe gesture is active
      // (touchStart was recorded). Taps without movement are left alone.
      if (touchStartRef.current !== null) {
        e.preventDefault();
      }
    };

    container.addEventListener('touchmove', blockScroll, { passive: false });
    return () => container.removeEventListener('touchmove', blockScroll);
  }, [isMobile]);

  // Load more snake pages when user approaches the end
  useEffect(() => {
    if (!snakeMetadata || !blogPost || loadingMoreSnakePages) return;

    const { snakeStartPageIndex, loadedSnakePages, totalSnakePages, prebuiltBlogId, hasMoreSnakePages, loadedStartIndex = 0 } = snakeMetadata;

    // In snake-only mode, snakeStartPageIndex is 0, so we calculate differently
    const actualSnakeStartIndex = isSnakeOnlyMode ? 0 : snakeStartPageIndex;
    const snakeEndIndex = actualSnakeStartIndex + loadedSnakePages - 1;
    const pagesUntilEnd = snakeEndIndex - currentPage;

    // Calculate the next start index for pagination (accounting for loadedStartIndex in snake-only mode)
    const nextStartIndex = loadedStartIndex + loadedSnakePages;

    if (hasMoreSnakePages && pagesUntilEnd <= 3 && currentPage >= actualSnakeStartIndex) {
      console.log(`🐍 Loading more snake pages (current: ${currentPage}, snake end: ${snakeEndIndex}, next start: ${nextStartIndex}, remaining: ${totalSnakePages - nextStartIndex})`);

      const loadMoreSnakePages = async () => {
        try {
          setLoadingMoreSnakePages(true);
          // Pass affiliateId and subcategoryId for randomization if no performance data
          const affiliateId = blogPost.author?._id || blogPost.author;
          const subcategoryId = blogPost.subcategoryId?._id || blogPost.subcategoryId;
          // Head blog id, so the backend's cascade excludes this blog from its
          // own (paginated) snake — same exclusion the initial load already does.
          const headBlogId = isSnakeOnlyMode ? null : blogPost._id;
          const response = await apiService.getSnakePages(
            prebuiltBlogId,
            nextStartIndex, // Use nextStartIndex instead of loadedSnakePages
            10,
            affiliateId,
            subcategoryId,
            headBlogId
          );

          if (response.pages && response.pages.length > 0) {
            // Parse current content
            const currentContent = JSON.parse(blogPost.content);
            const newPages = response.pages;

            // Calculate next page ID
            let nextPageId = currentContent.length > 0
              ? Math.max(...currentContent.map(p => p.id || 0)) + 1
              : 1;

            // Append new pages
            newPages.forEach(page => {
              currentContent.push({
                ...page,
                id: nextPageId++
              });
            });

            // Update blog post content
            setBlogPost(prev => ({
              ...prev,
              content: JSON.stringify(currentContent),
              _fetchedAt: Date.now()
            }));

            // Update snake metadata
            setSnakeMetadata(prev => ({
              ...prev,
              loadedSnakePages: prev.loadedSnakePages + newPages.length,
              hasMoreSnakePages: response.hasMore
            }));

            // Save current page position for this subcategory
            const currentSubcatId = blogPost.subcategoryId?.toString() || blogPost.subcategoryId;
            if (currentSubcatId) {
              setSubcategoryPagePositions(prev => ({
                ...prev,
                [currentSubcatId]: currentPage
              }));
            }

            console.log(`✅ Loaded ${newPages.length} more snake pages (total loaded: ${loadedSnakePages + newPages.length}/${totalSnakePages})`);
          }
        } catch (error) {
          console.error('❌ Error loading more snake pages:', error);
        } finally {
          setLoadingMoreSnakePages(false);
        }
      };

      loadMoreSnakePages();
    }
  }, [currentPage, snakeMetadata, blogPost, loadingMoreSnakePages]);

  // Track page views when currentPage changes.
  // Every page change counts. The backend handles deduplication if
  // initial page load tracking fires at the same time as this effect.
  useEffect(() => {
    console.log('🔄 [PAGE TRACKING EFFECT] Triggered - currentPage:', currentPage);

    // Read latest values from refs (avoids stale closure without adding as deps)
    const blogPostSnap = blogPostRef.current;
    const snakeMetaSnap = snakeMetadataRef.current;
    const prebuiltMappingSnap = prebuiltBlogPageMappingRef.current;

    // Only track if blog post is loaded
    if (!blogPostSnap || !blogPostSnap._id) {
      console.log('⚠️  [PAGE TRACKING] Blog post not loaded yet');
      return;
    }

    // Skip tracking for programmatic page jumps (URL param, last-page restore, subcategory switch).
    // loadBlogPost already tracks the initial page in those cases.
    if (skipNextPageTrackRef.current) {
      skipNextPageTrackRef.current = false;
      console.log('ℹ️  [PAGE TRACKING] Skipping programmatic page change — loadBlogPost handles tracking');
      return;
    }

    const blogPostId = blogPostSnap._id;
    const pageNumber = currentPage;

    // Rapid-fire cooldown: skip if this exact page was tracked within the last 1 second
    // (guards against accidental double-swipe or React StrictMode double-fire)
    const now = Date.now();
    const lastTrackedAt = lastSwipeTrackRef.current[pageNumber] || 0;
    if (now - lastTrackedAt < 1000) {
      console.log(`⚠️  [PAGE TRACKING] Cooldown active for page ${pageNumber} (${now - lastTrackedAt}ms since last track), skipping`);
      return;
    }

    // Record timestamp immediately (before async call) to guard against rapid double-fire
    lastSwipeTrackRef.current = { ...lastSwipeTrackRef.current, [pageNumber]: now };

    // Helper function to get correct affiliate for a page
    const getAffiliateForPage = (blogPost, pageNumber, snakeMeta, prebuiltMapping) => {
      // 1. Prioritize dynamically attached _sourceBlogAuthor from content pages
      try {
        const parsedContent = typeof blogPost.content === 'string' ? JSON.parse(blogPost.content) : blogPost.content;
        if (Array.isArray(parsedContent) && parsedContent.length > pageNumber) {
          const targetPage = parsedContent[pageNumber];
          if (targetPage && targetPage._sourceBlogAuthor) {
            console.log(`  📍 Using _sourceBlogAuthor for page ${pageNumber}: ${targetPage._sourceBlogAuthor}`);
            return targetPage._sourceBlogAuthor;
          }
        }
      } catch (error) {
        console.warn('Error parsing content for affiliate check:', error);
      }

      // 2. Check if blog has pageAffiliateMapping directly (for merged blogs)
      if (blogPost.pageAffiliateMapping && typeof blogPost.pageAffiliateMapping === 'object') {
        const pageKey = pageNumber.toString();
        if (blogPost.pageAffiliateMapping[pageKey]) {
          const mappedAffiliateId = blogPost.pageAffiliateMapping[pageKey];
          console.log(`  📍 Using pageAffiliateMapping for page ${pageNumber}: ${mappedAffiliateId}`);
          return mappedAffiliateId;
        }
      }

      // 3. Check prebuilt blog's pageAffiliateMapping (for dynamically attached snakes)
      if (prebuiltMapping && typeof prebuiltMapping === 'object' && snakeMeta) {
        // For snake pages, adjust page number to snake's index
        // Also handle snake-only mode where snakeStartPageIndex is 0
        const snakeStartIndex = snakeMeta.snakeStartPageIndex || 0;
        if (pageNumber >= snakeStartIndex) {
          const snakePageIndex = pageNumber - snakeStartIndex;
          const pageKey = snakePageIndex.toString();
          if (prebuiltMapping[pageKey]) {
            const mappedAffiliateId = prebuiltMapping[pageKey];
            console.log(`  📍 Using prebuilt blog pageAffiliateMapping for snake page ${pageNumber} (snake index ${snakePageIndex}): ${mappedAffiliateId}`);
            return mappedAffiliateId;
          }
        }
      }

      // 4. Fallback: use head blog's affiliate (blog author)
      const authorId = blogPost.author?._id || blogPost.author;
      console.log(`  📍 Using blog author for page ${pageNumber}: ${authorId}`);
      return authorId;
    };

    const affiliateId = getAffiliateForPage(blogPostSnap, pageNumber, snakeMetaSnap, prebuiltMappingSnap);

    // Check if current page has an offer element
    // Only count as offer view if the specific page contains an offer element
    let offerId = null;

    let currentPageElements = [];
    let sourceBlogId = null;
    let pageId = null;
    try {
      const parsedContent = JSON.parse(blogPostSnap.content);

      if (Array.isArray(parsedContent) && parsedContent.length > 0 && parsedContent[0].id && parsedContent[0].elements) {
        // New format: pages structure - get current page elements
        if (parsedContent[currentPage]) {
          currentPageElements = parsedContent[currentPage].elements || [];
          sourceBlogId = parsedContent[currentPage]._sourceBlogId || null;
          pageId = parsedContent[currentPage].id || parsedContent[currentPage]._id || null;
        }

      } else if (Array.isArray(parsedContent)) {
        // Old format: flat elements array - use createMobilePages
        const mobilePages = createMobilePages(parsedContent);
        if (mobilePages[currentPage]) {
          currentPageElements = mobilePages[currentPage] || [];
        }
      }
    } catch (error) {
      console.error('Error parsing blog content for offer check:', error);
    }

    const offerElement = currentPageElements.find(el => el.type === 'offer');

    // Determine whether this is a snake page so we can apply the right offerId logic.
    // For head pages: require hasAffiliateLinkInOfferElement (redirect URL in htmlContent)
    //   to avoid counting non-clickable offer elements as offer views.
    // For snake pages: snake affiliates' offer elements don't have the redirect URL pattern
    //   in htmlContent (they use their own link format), so we use offerId directly.
    const snakeStartIdx = snakeMetaSnap?.snakeStartPageIndex;
    const isSnakePage = snakeStartIdx !== undefined && pageNumber >= snakeStartIdx;

    if (offerElement) {
      if (isSnakePage) {
        // Snake page: trust offerId on the element directly (redirect URL check is unreliable)
        offerId = offerElement.offerId || null;
      } else {
        // Head page: only count as offer view if the element has a clickable redirect link
        if (hasAffiliateLinkInOfferElement(offerElement)) {
          offerId = offerElement.offerId || blogPostSnap.offerId?._id || blogPostSnap.offerId;
        }
      }
    }

    console.log(`  🎯 [PAGE TRACKING] offerId resolved: ${offerId || 'N/A'} (offerElement: ${!!offerElement}, isSnakePage: ${isSnakePage}, element.offerId: ${offerElement?.offerId || 'N/A'})`);

    const offerElementId = offerElement?.id || null;
    const sessionId = sessionTracking.getSessionId();

    console.log(`📊 [PAGE TRACKING] Tracking page view for page ${pageNumber}:`);
    console.log('  📝 Blog Post ID:', blogPostId);
    console.log('  👤 Affiliate ID (page owner):', affiliateId);
    console.log('  🎯 Offer ID:', offerId || 'N/A');
    console.log('  🆔 Session ID:', sessionId);
    console.log('  📄 Page Number:', pageNumber);
    console.log('  🐍 Source Blog ID:', sourceBlogId || 'N/A');
    console.log('  🧩 Offer Element ID:', offerElementId || 'N/A');
    console.log('  🐍 Snake metadata:', snakeMetaSnap);
    console.log('  📊 Page mapping:', prebuiltMappingSnap || blogPostSnap.pageAffiliateMapping || 'none');

    // (Rapid-fire timestamp already recorded above before the async call)

    // Track page view (don't await - fire and forget)
    apiService.trackBlogPageView(
      blogPostId,
      affiliateId,
      offerId,
      sessionId,
      pageNumber,
      sourceBlogId,
      offerElementId,
      pageId
    ).then(result => {
      console.log(`✅ [PAGE TRACKING] Page ${pageNumber} view tracked successfully:`, result);
    }).catch(error => {
      console.error(`❌ [PAGE TRACKING] Error tracking page ${pageNumber} view:`, error);
      console.error('  Error details:', error.response?.data || error.message);
      // Remove from tracked set on error so it can retry on next navigation
      lastSwipeTrackRef.current[pageNumber] = 0;
      // Don't show error to user, just log it
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]); // Only re-run when user navigates to a new page

  // Reset swipe cooldown and page mapping when blog post changes
  useEffect(() => {
    lastSwipeTrackRef.current = {}; // Clear cooldown map for new blog
    skipNextPageTrackRef.current = false; // Clear any stale programmatic-jump flag for new blog
    // Reset page mapping when blog changes
    setPrebuiltBlogPageMapping(null);
  }, [blogPost?._id]);

  // Create pages for mobile - smart grouping with space-aware logic
  const createMobilePages = (elements) => {
    if (!elements || elements.length === 0) return [];

    const pages = [];
    let currentPageElements = [];
    let currentPageHeight = 0;
    const maxPageHeight = Math.min(window.innerHeight - 200, 600); // More conservative height limit

    // More accurate element height estimation
    const getElementHeight = (element) => {
      let height;
      switch (element.type) {
        case 'title':
          height = 80; // Reduced for better fitting
          break;
        case 'text':
          const textLength = element.content?.length || 0;
          // More dynamic text height - shorter text gets less space
          height = Math.max(60, Math.min(150, textLength * 0.8));
          break;
        case 'media':
          height = 300; // Reduced for better fitting
          break;
        case 'button':
          height = 60; // Fixed height for buttons
          break;
        case 'offer':
          // Dynamic offer height based on content
          const offerContentLength = element.htmlContent?.length || 0;
          height = Math.max(150, Math.min(250, offerContentLength * 0.3));
          break;
        default:
          height = 80;
          break;
      }
      return height;
    };

    elements.forEach((element, index) => {
      const elementHeight = getElementHeight(element);
      const isLargeElement = element.type === 'media' || element.type === 'offer';
      const isTextElement = element.type === 'text' || element.type === 'title';

      // Check if element fits on current page
      const wouldFitOnCurrentPage = (currentPageHeight + elementHeight) <= maxPageHeight;


      // Grouping logic
      if (currentPageElements.length === 0) {
        // First element on page - always add it
        currentPageElements.push(element);
        currentPageHeight += elementHeight;
      } else if (wouldFitOnCurrentPage) {
        // Element fits - check compatibility
        const hasLargeElement = currentPageElements.some(el => el.type === 'media' || el.type === 'offer');
        const hasTextElement = currentPageElements.some(el => el.type === 'text' || el.type === 'title');

        if (isLargeElement && hasLargeElement) {
          // Never allow two large elements on the same page
          pages.push([...currentPageElements]);
          currentPageElements = [element];
          currentPageHeight = elementHeight;
        } else if (isTextElement && hasLargeElement) {
          // Text with large element - allow up to 2 text elements with 1 large element
          const textCount = currentPageElements.filter(el => el.type === 'text' || el.type === 'title').length;
          const largeCount = currentPageElements.filter(el => el.type === 'media' || el.type === 'offer').length;

          if (textCount < 2 && largeCount === 1 && (currentPageHeight + elementHeight) <= (maxPageHeight * 0.8)) {
            currentPageElements.push(element);
            currentPageHeight += elementHeight;
          } else {
            // Start new page
            pages.push([...currentPageElements]);
            currentPageElements = [element];
            currentPageHeight = elementHeight;
          }
        } else if (isLargeElement && hasTextElement) {
          // Large element with existing text - allow if we have ≤ 2 text elements total
          const textCount = currentPageElements.filter(el => el.type === 'text' || el.type === 'title').length;
          const largeCount = currentPageElements.filter(el => el.type === 'media' || el.type === 'offer').length;

          console.log(`Large element with text - textCount: ${textCount}, largeCount: ${largeCount}`);
          console.log(`Height check: ${currentPageHeight + elementHeight} <= ${maxPageHeight * 0.85} = ${(currentPageHeight + elementHeight) <= (maxPageHeight * 0.85)}`);

          if (textCount <= 2 && largeCount === 0 && (currentPageHeight + elementHeight) <= (maxPageHeight * 0.85)) {
            console.log(`✅ Adding large element to existing text elements`);
            currentPageElements.push(element);
            currentPageHeight += elementHeight;
          } else {
            console.log(`❌ Starting new page for large element`);
            // Start new page
            pages.push([...currentPageElements]);
            currentPageElements = [element];
            currentPageHeight = elementHeight;
          }
        } else if (isTextElement && hasTextElement) {
          // Multiple text elements - limit to 2 per page and check height strictly
          const textCount = currentPageElements.filter(el => el.type === 'text' || el.type === 'title').length;
          if (textCount < 2 && (currentPageHeight + elementHeight) <= (maxPageHeight * 0.6)) {
            currentPageElements.push(element);
            currentPageHeight += elementHeight;
          } else {
            // Too many text elements or height exceeded, start new page
            pages.push([...currentPageElements]);
            currentPageElements = [element];
            currentPageHeight = elementHeight;
          }
        } else {
          // Default case - allow up to 3 elements total (2 text + 1 large or 3 text)
          const textCount = currentPageElements.filter(el => el.type === 'text' || el.type === 'title').length;
          const largeCount = currentPageElements.filter(el => el.type === 'media' || el.type === 'offer').length;

          if (currentPageElements.length < 3 &&
            ((textCount + largeCount) < 3) &&
            (currentPageHeight + elementHeight) <= (maxPageHeight * 0.75)) {
            currentPageElements.push(element);
            currentPageHeight += elementHeight;
          } else {
            pages.push([...currentPageElements]);
            currentPageElements = [element];
            currentPageHeight = elementHeight;
          }
        }
      } else {
        // Element doesn't fit - start new page
        pages.push([...currentPageElements]);
        currentPageElements = [element];
        currentPageHeight = elementHeight;
      }
    });

    // Add remaining elements as final page
    if (currentPageElements.length > 0) {
      pages.push(currentPageElements);
    }

    return pages;
  };

  // Helper function to check if an offer element contains an affiliate link
  const hasAffiliateLinkInOfferElement = (offerElement) => {
    if (!offerElement) return false;

    // Check if htmlContent contains the affiliate redirect URL pattern
    const htmlContent = offerElement.htmlContent || '';
    // Check for the affiliate redirect pattern: /api/affiliate/redirect/
    const hasAffiliateLink = /\/api\/affiliate\/redirect\//.test(htmlContent);

    return hasAffiliateLink;
  };

  // Calculate pages from blogPost content
  const pages = useMemo(() => {
    if (!blogPost || !blogPost.content) {
      console.log('📄 [PAGES] No blog post or content');
      return [];
    }

    console.log(`📄 [PAGES] Recalculating pages - content length: ${blogPost.content?.length}, fetchedAt: ${blogPost._fetchedAt}`);

    try {
      const parsedContent = JSON.parse(blogPost.content);

      // Check if content is in new pages format or old elements format
      if (Array.isArray(parsedContent) && parsedContent.length > 0 && parsedContent[0].id && parsedContent[0].elements) {
        // New format: pages structure - preserve full page objects with metadata
        const pagesResult = parsedContent.map(page => ({
          ...page, // Preserve all metadata (_sourceBlogTitle, _sourceBlogAuthorInfo, etc.)
          elements: page.elements || []
        }));
        console.log(`📄 [PAGES] Parsed ${pagesResult.length} pages from new format`);
        console.log(`📄 [PAGES] First page metadata:`, pagesResult[0] ? {
          hasElements: !!pagesResult[0].elements,
          hasSourceBlogTitle: !!pagesResult[0]._sourceBlogTitle,
          hasSourceBlogAuthorInfo: !!pagesResult[0]._sourceBlogAuthorInfo,
          keys: Object.keys(pagesResult[0])
        } : 'No pages');
        return pagesResult;
      } else if (Array.isArray(parsedContent)) {
        // Old format: flat elements array (backward compatibility)
        const pagesResult = createMobilePages(parsedContent);
        console.log(`📄 [PAGES] Created ${pagesResult.length} pages from old format`);
        return pagesResult;
      }
    } catch (error) {
      console.error('📄 [PAGES] Error parsing blog content:', error);
    }

    console.log('📄 [PAGES] Returning empty pages array');
    return [];
  }, [blogPost?.content, blogPost?._id, blogPost?._fetchedAt]); // Include timestamp to force recalculation

  const blogStartPageIndices = useMemo(
    () => getSortedBlogStartPageIndices(pages, snakeMetadata),
    [pages, snakeMetadata]
  );

  // Load page from URL parameter or backend-stored last page on initial load
  useEffect(() => {
    if (!blogPost || pages.length === 0) return;

    const pageParam = searchParams.get('page');

    // Priority 1: URL parameter (if present) - use directly, no API call
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      // Validate page number (0-indexed, so 0 to pages.length - 1)
      if (!isNaN(pageNumber) && pageNumber >= 0 && pageNumber < pages.length) {
        console.log(`📄 [URL NAVIGATION] Loading page ${pageNumber} from URL parameter (no API call)`);
        isLoadingFromUrlRef.current = true;
        if (pageNumber !== currentPage) {
          skipNextPageTrackRef.current = true; // programmatic jump from URL param — loadBlogPost handles tracking
          setCurrentPage(pageNumber);
        }

        // Reset flag after a short delay to allow state update
        setTimeout(() => {
          isLoadingFromUrlRef.current = false;
        }, 100);
        return; // Exit early - don't call API when URL parameter is present
      } else {
        console.log(`⚠️  [URL NAVIGATION] Invalid page number ${pageNumber}, removing from URL and checking backend`);
        // Remove invalid page parameter and continue to check backend
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('page');
          return newParams;
        });
        // Fall through to API call below
      }
    }

    // Priority 2: Backend-stored last page (only when NO URL parameter - fresh browser start)
    // This only runs when there's no page parameter in the URL
    const loadLastPage = async () => {
      try {
        console.log(`📄 [LAST PAGE] No URL parameter found - fetching last visited page from backend for blog ${blogPost._id}`);
        const result = await apiService.getBlogLastPage(blogPost._id);

        if (result.success && result.lastPageNumber > 0) {
          const lastPageNumber = result.lastPageNumber;
          // Validate page number is still within bounds
          if (lastPageNumber >= 0 && lastPageNumber < pages.length) {
            console.log(`📄 [LAST PAGE] Loading last visited page ${lastPageNumber} from backend (fresh browser start)`);
            isLoadingFromUrlRef.current = true;
            if (lastPageNumber !== currentPage) {
              skipNextPageTrackRef.current = true; // programmatic jump to last page — loadBlogPost handles tracking
              setCurrentPage(lastPageNumber);
            }

            // Update URL to reflect the loaded page
            setSearchParams(prev => {
              const newParams = new URLSearchParams(prev);
              newParams.set('page', lastPageNumber.toString());
              return newParams;
            }, { replace: true });
            // Reset flag after a short delay
            setTimeout(() => {
              isLoadingFromUrlRef.current = false;
            }, 100);
          } else {
            console.log(`⚠️  [LAST PAGE] Last page ${lastPageNumber} is out of bounds (max: ${pages.length - 1}), defaulting to page 0`);
          }
        } else {
          console.log(`📄 [LAST PAGE] No last page found or page is 0, starting from page 0`);
        }
      } catch (error) {
        console.error('❌ [LAST PAGE] Error loading last visited page:', error);
        // Don't fail, just start from page 0
      }
    };

    loadLastPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogPost?._id, pages.length]); // Only run when blogPost ID or pages length changes

  // Update URL when currentPage changes (but not on initial load from URL)
  useEffect(() => {
    if (!blogPost || pages.length === 0) return;

    // Skip URL update if we're currently loading from URL
    if (isLoadingFromUrlRef.current) {
      return;
    }

    const currentPageParam = searchParams.get('page');
    const currentPageNumber = currentPageParam ? parseInt(currentPageParam, 10) : 0;

    // Only update URL if currentPage is different from URL parameter
    if (currentPage !== currentPageNumber) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (currentPage === 0) {
          // Remove page parameter for page 0 (cleaner URL)
          newParams.delete('page');
        } else {
          newParams.set('page', currentPage.toString());
        }
        return newParams;
      }, { replace: true }); // Use replace to avoid adding to history
    }
  }, [currentPage, blogPost, pages.length, searchParams, setSearchParams]);

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    const touch = e.targetTouches[0];
    const touchY = touch.clientY;
    const touchX = touch.clientX;
    console.log('📱 [SWIPE] Touch start:', { x: touchX, y: touchY });
    touchStartRef.current = touchY;
    touchEndRef.current = null;
    touchStartXRef.current = touchX;
    touchEndXRef.current = null;
    // NOTE: setTouchStart / setTouchEnd (state) are intentionally NOT called here.
    // All swipe logic reads from refs; calling state setters would cause a
    // React re-render on every touchmove frame, competing with Framer Motion.
  };

  const handleTouchMove = (e) => {
    const touch = e.targetTouches[0];
    // Update refs only — no state update to avoid per-frame re-renders.
    touchEndRef.current = touch.clientY;
    touchEndXRef.current = touch.clientX;
  };

  const handleTouchEnd = (totalPages, blogStarts) => {
    const startY = touchStartRef.current;
    const endY = touchEndRef.current;
    const startX = touchStartXRef.current;
    const endX = touchEndXRef.current;
    const currentPageValue = currentPageRef.current;
    const snakeMeta = snakeMetadataRef.current;
    const hasSnake = snakeMeta != null && snakeMeta.snakeStartPageIndex !== undefined;

    console.log('📱 [SWIPE] Touch end - touchStart:', { x: startX, y: startY }, 'touchEnd:', { x: endX, y: endY }, 'page:', currentPageValue, 'totalPages:', totalPages, 'hasSnake:', hasSnake);

    if (startY == null || endY == null || startX == null || endX == null) {
      console.log('⚠️  [SWIPE] Missing touch start or end, resetting');
      touchStartRef.current = null;
      touchEndRef.current = null;
      touchStartXRef.current = null;
      touchEndXRef.current = null;
      return;
    }

    const distanceY = startY - endY;
    const distanceX = endX - startX;
    const minSwipe = 50;
    const absX = Math.abs(distanceX);
    const absY = Math.abs(distanceY);
    const isUpSwipe = distanceY > minSwipe;
    const isDownSwipe = distanceY < -minSwipe;
    const isLeftSwipe = distanceX < -minSwipe;
    const isRightSwipe = distanceX > minSwipe;
    // Strict axis: vertical only wins if |dy| > |dx| so swipe-up on page 0 isn’t eaten as “horizontal”.
    // On ties, prefer vertical when |dy| is a real swipe so first-page “up” still hits next blog.
    let isHorizontalDominant = false;
    let isVerticalDominant = false;
    if (absX > minSwipe || absY > minSwipe) {
      if (absY > absX && absY > minSwipe) {
        isVerticalDominant = true;
      } else if (absX > absY && absX > minSwipe) {
        isHorizontalDominant = true;
      } else if (absY >= minSwipe) {
        isVerticalDominant = true;
      } else if (absX > minSwipe) {
        isHorizontalDominant = true;
      }
    }

    console.log('📱 [SWIPE] Distances:', { distanceY, distanceX, isUpSwipe, isDownSwipe, isLeftSwipe, isRightSwipe, isVerticalDominant, isHorizontalDominant });

    const blogStartsResolved = blogStarts && blogStarts.length > 0 ? blogStarts : [0];
    const { segmentStart, segmentEnd } = getBlogSegmentBoundsForPage(
      currentPageValue,
      blogStartsResolved,
      totalPages
    );

    // Horizontal: current blog segment only (next/prev blog = swipe up/down).
    // Left = next page, right = previous page (natural "swipe to advance"
    // direction — opposite of the next/previous page arrow buttons
    // (blog-next-page / blog-previous-page) below, which are unchanged).
    if (isHorizontalDominant && totalPages > 1) {
      if (isLeftSwipe && currentPageValue < segmentEnd) {
        console.log('📱 [SWIPE] Swipe left — next page in segment:', currentPageValue + 1, { segmentStart, segmentEnd });
        prevPageRef.current = currentPageValue;
        setPageSlideAxis('x');
        setCurrentPage(currentPageValue + 1);
      } else if (isRightSwipe && currentPageValue > segmentStart) {
        console.log('📱 [SWIPE] Swipe right — previous page in segment:', currentPageValue - 1, { segmentStart, segmentEnd });
        prevPageRef.current = currentPageValue;
        setPageSlideAxis('x');
        setCurrentPage(currentPageValue - 1);
      }
    } else if (
      isVerticalDominant &&
      totalPages > 1 &&
      (hasSnake || blogStartsResolved.length > 1)
    ) {
      if (isUpSwipe) {
        const nextP = getNextBlogNavPage(currentPageValue, blogStartsResolved);
        if (nextP != null) {
          console.log('📱 [SWIPE] Swipe up — next blog segment (first page):', nextP);
          prevPageRef.current = currentPageValue;
          setPageSlideAxis('y');
          setCurrentPage(nextP);
        } else if (currentPageValue === totalPages - 1) {
          // At the very end of everything (including snake)
          console.log('📱 [SWIPE] Swipe up at end — navigating to next subcategory blog');
          handleNextSubcategory();
        } else {
          console.log('📱 [SWIPE] Swipe up ignored (already at end)');
        }
      } else if (isDownSwipe) {
        const prevP = getPreviousBlogNavPage(currentPageValue, blogStartsResolved);
        if (prevP != null) {
          console.log('📱 [SWIPE] Swipe down — previous blog start:', prevP);
          prevPageRef.current = currentPageValue;
          setPageSlideAxis('y');
          setCurrentPage(prevP);
        } else {
          console.log('📱 [SWIPE] Swipe down ignored (already at first blog)');
        }
      } else {
        console.log('📱 [SWIPE] Swipe ignored (vertical but not up/down enough)');
      }
    } else if (isVerticalDominant && totalPages > 1) {
      console.log('📱 [SWIPE] Swipe ignored (no multi-blog vertical target)');
    } else {
      console.log('📱 [SWIPE] Swipe ignored (ambiguous diagonal)');
    }

    // Reset touch refs (no state update — see handleTouchStart comment).
    touchStartRef.current = null;
    touchEndRef.current = null;
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  // Removed renderElement function - now using shared BlogElementRenderer component

  // Keep this placeholder function for compatibility (no longer used)
  const renderElement = (element, index) => {
    // Get padding values from element styles or use defaults
    const elementPadding = {
      top: element.styles?.paddingTop || element.styles?.top || element.styles?.py || 1,
      bottom: element.styles?.paddingBottom || element.styles?.bottom || element.styles?.py || 1,
      left: element.styles?.paddingLeft || element.styles?.left || element.styles?.px || 2,
      right: element.styles?.paddingRight || element.styles?.right || element.styles?.px || 2
    };

    // Get size from element styles or use default
    const elementSize = element.styles?.size || 100;

    switch (element.type) {
      case 'title':
        return element.content ? (
          (() => {
            // Check if content contains HTML tags
            const hasHtmlTags = /<[^>]+>/.test(element.content);

            const commonStyles = {
              margin: 0,
              fontSize: isSmallMobile ? '1.2rem' : '1.4rem',
              textAlign: 'center',
              transform: `scale(${elementSize / 100})`,
              transformOrigin: 'center',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
              '& *': {
                maxWidth: '100%',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              },
              pt: `${elementPadding.top}rem`,
              pb: `${elementPadding.bottom}rem`,
              pl: `${elementPadding.left}rem`,
              pr: `${elementPadding.right}rem`,
              ...element.styles
            };

            if (hasHtmlTags) {
              return (
                <Box
                  key={element.id || index}
                  sx={commonStyles}
                  dangerouslySetInnerHTML={{ __html: element.content }}
                />
              );
            }

            return (
              <Typography
                key={element.id || index}
                variant="h4"
                sx={{
                  ...commonStyles,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {element.content}
              </Typography>
            );
          })()
        ) : null;

      case 'text':
        return element.content ? (
          (() => {
            // Check if content contains HTML tags
            const hasHtmlTags = /<[^>]+>/.test(element.content);

            const commonStyles = {
              margin: 0,
              lineHeight: 1.6,
              fontSize: isSmallMobile ? '0.9rem' : '1rem',
              textAlign: element.styles?.textAlign || 'left',
              transform: `scale(${elementSize / 100})`,
              transformOrigin: 'center',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
              '& *': {
                maxWidth: '100%',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              },
              pt: `${elementPadding.top}rem`,
              pb: `${elementPadding.bottom}rem`,
              pl: `${elementPadding.left}rem`,
              pr: `${elementPadding.right}rem`,
              ...element.styles
            };

            if (hasHtmlTags) {
              return (
                <Box
                  key={element.id || index}
                  sx={commonStyles}
                  dangerouslySetInnerHTML={{ __html: element.content }}
                />
              );
            }

            return (
              <Typography
                key={element.id || index}
                variant="body1"
                sx={{
                  ...commonStyles,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {element.content}
              </Typography>
            );
          })()
        ) : null;

      case 'media':
        return element.mediaUrl ? (
          <Box
            key={element.id || index}
            className={element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? "media-video-container" : "media-image-container"}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              // Full width - extend beyond container padding for media elements
              mx: isMobile ? '-16px' : 0, // Extend beyond container padding (px: 2 = 16px) only on mobile
              pt: `${elementPadding.top}rem`,
              pb: `${elementPadding.bottom}rem`,
              transform: `scale(${elementSize / 100})`,
              transformOrigin: 'center'
            }}
          >
            <Box sx={{
              width: '100%',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? (
                <video
                  src={element.mediaUrl}
                  controls
                  playsInline
                  webkit-playsinline="true"
                  poster={element.thumbnailUrl || undefined}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: element.styles?.objectFit || 'contain',
                    borderRadius: isMobile ? '0px' : (element.styles?.borderRadius || '0px'),
                    boxShadow: element.styles?.boxShadow || 'none',
                    border: element.styles?.border || 'none',
                    padding: element.styles?.padding || '0px',
                    aspectRatio: element.styles?.aspectRatio || 'auto',
                    display: 'block'
                  }}
                />
              ) : (
                <img
                  src={element.mediaUrl}
                  alt="Blog media"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: element.styles?.objectFit || 'contain',
                    borderRadius: element.styles?.borderRadius || '0px',
                    boxShadow: element.styles?.boxShadow || 'none',
                    border: element.styles?.border || 'none',
                    padding: element.styles?.padding || '0px',
                    aspectRatio: element.styles?.aspectRatio || 'auto',
                    display: 'block'
                  }}
                />
              )}
            </Box>
          </Box>
        ) : null;

      case 'button':
        return element.content ? (
          <Box
            key={element.id || index}
            sx={{
              mb: 2,
              textAlign: element.styles?.textAlign || 'center',
              px: 2
            }}
          >
            <Button
              variant="contained"
              href={element.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: element.styles?.backgroundColor || '#1976d2',
                color: element.styles?.color || 'white',
                fontSize: isSmallMobile ? '0.85rem' : '0.95rem',
                fontWeight: element.styles?.fontWeight || 'bold',
                padding: element.styles?.padding || '10px 20px',
                borderRadius: element.styles?.borderRadius || '4px',
                textTransform: element.styles?.textTransform || 'none',
                transform: `scale(${elementSize / 100})`,
                transformOrigin: 'center',
                '&:hover': {
                  backgroundColor: element.styles?.hoverBackgroundColor || element.styles?.backgroundColor || '#1565c0'
                },
                ...element.styles
              }}
            >
              {element.content}
            </Button>
          </Box>
        ) : null;

      case 'offer':
        return element.htmlContent ? (
          <Box
            key={element.id || index}
            sx={{
              width: '100%',
              height: 'auto',
              // Full width - extend beyond container padding (Paper has no padding in mobile)
              mx: 0,
              my: 0,
              mt: 0, // No top margin - ensure border aligns with container top
              mb: 0, // No bottom margin
              transform: `scale(${elementSize / 100})`,
              transformOrigin: 'top center' // Align transform origin to top
            }}
          >
            <Box
              sx={{
                width: '100%',
                pt: 0, // No top padding
                pb: 1, // Reduced bottom padding
                px: 2, // Horizontal padding
                '& > *:first-child': {
                  mt: 0, // Remove top margin from first child
                  pt: 0 // Remove top padding from first child
                },
                border: 'none !important', // Remove any borders
                outline: 'none !important', // Remove any outlines
                // Preserve original HTML layout and styling - no CSS overrides
                '& *': {
                  // Only ensure images don't overflow container, but preserve their original styling
                  maxWidth: '100%',
                  border: 'none !important', // Remove borders from all child elements
                  outline: 'none !important' // Remove outlines from all child elements
                },
                // Ensure videos take full width
                '& video, & iframe[src*="youtube"], & iframe[src*="vimeo"], & iframe': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                  height: 'auto !important',
                  display: 'block !important',
                  margin: '0 !important',
                  padding: '0 !important'
                },
                // Ensure video wrapper divs take full width (only for videos)
                // BUT preserve padding-bottom for aspect ratio containers
                '& div:has(> video) div[style*="position: relative"]': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                  margin: '0 !important',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important',
                  // CRITICAL: Don't override paddingBottom - it's needed for aspect ratio
                  // The inline style sets padding-bottom as a percentage, which creates the aspect ratio
                },
                // Explicitly preserve padding-bottom for video aspect ratio containers
                '& .video-aspect-ratio-container': {
                  position: 'relative !important',
                  height: '0 !important',
                  width: '100% !important',
                  maxWidth: '100% !important',
                  margin: '0 !important',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important',
                  paddingTop: '0 !important',
                  // paddingBottom is set via inline style with !important, so it should take precedence
                },
                // Ensure parent wrapper divs also take full width (only for videos)
                '& div:has(> video) div[style*="text-align: center"]': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                  margin: '0 !important',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important'
                },
                // Target divs that contain videos specifically - apply to all ancestor divs
                '& div:has(> video)': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                  margin: '0 !important',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important'
                },
                '& div:has(video)': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                  margin: '0 !important',
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important'
                }
              }}
              dangerouslySetInnerHTML={{ __html: element.htmlContent }}
            />
          </Box>
        ) : null;

      case 'blackbar':
        return (
          <Box
            key={element.id || index}
            sx={{
              mb: 3,
              backgroundColor: element.styles?.backgroundColor || '#000000',
              height: element.styles?.height || '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: element.styles?.borderRadius || '0px',
              px: 3,
              transform: `scale(${elementSize / 100})`,
              transformOrigin: 'center',
              ...element.styles
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {(element.styles?.buttons || []).map((button, buttonIndex) => (
                <Button
                  key={buttonIndex}
                  variant={button.variant || 'contained'}
                  color={button.color || 'primary'}
                  size={button.size || 'medium'}
                  sx={{
                    backgroundColor: button.backgroundColor || 'inherit',
                    color: button.color || 'white',
                    '&:hover': {
                      backgroundColor: button.hoverColor || 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {button.text || 'Button'}
                </Button>
              ))}
              {(element.styles?.buttons || []).length === 0 && (
                <Typography variant="body2" color="white" sx={{ opacity: 0.7 }}>
                  Black Bar
                </Typography>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  // Render mobile pagination
  const renderMobilePagination = (pages) => {
    if (!pages || pages.length === 0) return null;

    const blogStartsForNav =
      blogStartPageIndices.length > 0 ? blogStartPageIndices : [0];

    const navSeg = getBlogSegmentBoundsForPage(
      currentPage,
      blogStartsForNav,
      pages.length
    );
    const showPrevNav = currentPage > navSeg.segmentStart;
    const showNextNav = currentPage < navSeg.segmentEnd;

    const navRailCommon = {
      position: isMobile ? 'fixed' : 'absolute',
      top: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      zIndex: 1600,
      pointerEvents: 'none',
    };

    return (
      <Box
        ref={containerRef}
        sx={{
          height: isMobile ? '100dvh' : '100vh', // Full viewport height on desktop for scrolling
          width: '100%',
          position: 'relative',
          overflow: isMobile ? 'hidden' : 'auto', // Hidden on mobile, auto scroll on desktop
          backgroundColor: isMobile ? 'white' : '#f5f5f5', // White on mobile, gray on desktop
          paddingBottom: 0, // Remove any bottom padding
          paddingTop: isMobile ? 0 : 2, // Add top padding on desktop
          paddingX: isMobile ? 0 : 4, // Add more horizontal padding on desktop for phone frames
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center' // Center pages horizontally
        }}
              // Capture phase: video/media on page 0 often steals bubbling touches; we still need swipe coords.
              onTouchStartCapture={(e) => {
                if (isMobile) {
                  handleTouchStart(e);
                }
              }}
              onTouchMoveCapture={(e) => {
                if (isMobile) {
                  handleTouchMove(e);
                }
              }}
              onTouchEndCapture={(e) => {
                if (isMobile) {
                  const t = e.changedTouches?.[0];
                  if (t) {
                    touchEndRef.current = t.clientY;
                    touchEndXRef.current = t.clientX;
                  }
                  handleTouchEnd(pages.length, blogStartPageIndices);
                }
              }}
              onTouchCancelCapture={() => {
                if (isMobile) {
                  touchStartRef.current = null;
                  touchEndRef.current = null;
                  touchStartXRef.current = null;
                  touchEndXRef.current = null;
                }
              }}
            >

              {/* Pages */}
              {pages.map((pageData, pageIndex) => {
                // Handle both old format (array of elements) and new format (object with elements and metadata)
                const pageElements = Array.isArray(pageData) ? pageData : (pageData.elements || []);
                const pageMetadata = Array.isArray(pageData) ? null : pageData;
                const isSnakePage = snakeMetadata && snakeMetadata.snakeStartPageIndex !== undefined && pageIndex >= snakeMetadata.snakeStartPageIndex;
                const sourceBlogTitle = pageMetadata?._sourceBlogTitle;
                const sourceBlogAuthorId = pageMetadata?._sourceBlogAuthor;
                const sourceBlogAuthorInfo = pageMetadata?._sourceBlogAuthorInfo;
                const sourceBlogId = pageMetadata?._sourceBlogId;
                const pageId = pageMetadata?.id || pageMetadata?._id || null;

                // Terminal "make Tool Vids with us" CTA card — the last page of
                // an exhausted cascade (see prebuilt.service.js's
                // buildCascadingSnakeFeed / buildEndCardPage). It replaces the
                // old behavior of looping tier 1's best performers back to the
                // top once the feed ran out of new content. Rendered as its own
                // simple full-bleed image rather than going through the normal
                // head/snake layout below, since there's no real blog content,
                // author, or offer to show for it.
                if (pageMetadata?.isEndCard) {
                  const EndCardOuter = isMobile ? MotionBox : Box;
                  const endCardMotionProps = isMobile
                    ? {
                      initial: false,
                      animate: pageSlideAxis === 'x'
                        ? { x: `${(pageIndex - currentPage) * 100}%`, y: 0 }
                        : { y: `${(pageIndex - currentPage) * 100}%`, x: 0 },
                      transition: prefersReducedMotion
                        ? { type: 'tween', duration: 0.01, ease: 'linear' }
                        : (pageSlideAxis === 'x' ? HORIZONTAL_PAGE_WHOOSH : VERTICAL_BLOG_WHOOSH)
                    }
                    : {};

                  return (
                    <EndCardOuter
                      key={pageIndex}
                      {...endCardMotionProps}
                      sx={{
                        position: isMobile ? 'absolute' : 'relative',
                        top: isMobile ? 0 : 'auto',
                        left: isMobile ? 0 : 'auto',
                        right: isMobile ? 0 : 'auto',
                        height: isMobile ? '100%' : 'auto',
                        ...(!isMobile ? { transform: 'none', transition: 'none' } : { willChange: 'transform' }),
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: isMobile ? 0 : 3,
                        width: '100%',
                        alignItems: 'center'
                      }}
                    >
                      <Paper
                        onClick={handleAffiliateSignup}
                        title="Want to make Tool Vids with us?"
                        sx={{
                          flex: 1,
                          backgroundColor: '#ffffff',
                          borderRadius: 0,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          position: 'relative',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          width: '375px',
                          height: isMobile ? '100%' : 'auto',
                          minHeight: isMobile ? '100%' : '667px',
                          maxWidth: '375px',
                          margin: isMobile ? 0 : '20px auto',
                          border: '12px solid #1a1a1a',
                          borderTopWidth: '24px',
                          borderBottomWidth: '24px',
                          borderLeftWidth: '8px',
                          borderRightWidth: '8px',
                          '@media (max-width: 768px)': {
                            margin: 0,
                            width: '100%',
                            maxWidth: '100%',
                            height: '100%',
                            minHeight: '100%',
                            border: 'none',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        <img
                          src={pageMetadata._endCardImage || '/end-card-make-vids.png'}
                          alt="Want to make Tool Vids with us? Click the Make Vids button to get started."
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      </Paper>
                    </EndCardOuter>
                  );
                }

                // Helper function to get correct affiliate ID for this page (same logic as tracking)
                const getAffiliateForPage = (pageNum) => {
                  // 1. Prioritize dynamically attached _sourceBlogAuthor from content pages
                  if (sourceBlogAuthorId) {
                    return sourceBlogAuthorId;
                  }

                  // 2. Check if blog has pageAffiliateMapping directly (for merged blogs)
                  if (blogPost.pageAffiliateMapping && typeof blogPost.pageAffiliateMapping === 'object') {
                    const pageKey = pageNum.toString();
                    if (blogPost.pageAffiliateMapping[pageKey]) {
                      const mappedAffiliateId = blogPost.pageAffiliateMapping[pageKey];
                      return mappedAffiliateId;
                    }
                  }

                  // 3. Check prebuilt blog's pageAffiliateMapping (for dynamically attached snakes)
                  if (prebuiltBlogPageMapping && typeof prebuiltBlogPageMapping === 'object' && snakeMetadata) {
                    const snakeStartIndex = snakeMetadata.snakeStartPageIndex || 0;
                    if (pageNum >= snakeStartIndex) {
                      const snakePageIndex = pageNum - snakeStartIndex;
                      const pageKey = snakePageIndex.toString();
                      if (prebuiltBlogPageMapping[pageKey]) {
                        const mappedAffiliateId = prebuiltBlogPageMapping[pageKey];
                        return mappedAffiliateId;
                      }
                    }
                  }

                  // 4. Fallback: use head blog's affiliate (blog author)
                  return blogPost.author?._id || blogPost.author;
                };

                // Get the correct affiliate ID for this page
                const pageAffiliateId = getAffiliateForPage(pageIndex);

                // Get the correct offer ID for this page (from offer element if present, otherwise from blog)
                const getOfferIdForPage = () => {
                  // Check if this page has an offer element
                  const offerElement = pageElements.find(el => el.type === 'offer');
                  if (offerElement && offerElement.offerId) {
                    return offerElement.offerId;
                  }
                  // Fallback to blog-level offerId
                  return blogPost.offerId?._id || blogPost.offerId;
                };

                const pageOfferId = getOfferIdForPage();

                // Check if this is the first page of the original blog within the snake
                const isFirstPageOfOriginalBlog = isSnakePage && (() => {
                  // If this is the first snake page overall, it's definitely the first page of its blog
                  if (pageIndex === (snakeMetadata?.snakeStartPageIndex || 0)) {
                    return true;
                  }
                  // Otherwise, check if the previous page has a different sourceBlogId
                  const previousPageData = pages[pageIndex - 1];
                  const previousPageMetadata = Array.isArray(previousPageData) ? null : previousPageData;
                  const previousSourceBlogId = previousPageMetadata?._sourceBlogId;
                  return sourceBlogId && sourceBlogId !== previousSourceBlogId;
                })();

                // Debug logging for snake pages
                if (isSnakePage && pageIndex === (snakeMetadata?.snakeStartPageIndex || 0)) {
                  console.log(`🐍 First snake page (${pageIndex}) metadata:`, {
                    hasMetadata: !!pageMetadata,
                    sourceBlogTitle,
                    sourceBlogAuthorId,
                    sourceBlogAuthorInfo: sourceBlogAuthorInfo ? {
                      _id: sourceBlogAuthorInfo._id,
                      username: sourceBlogAuthorInfo.username,
                      name: sourceBlogAuthorInfo.name,
                      email: sourceBlogAuthorInfo.email,
                      hasProfileIcon: !!sourceBlogAuthorInfo.profileIcon,
                      profileIconType: typeof sourceBlogAuthorInfo.profileIcon,
                      profileIcon: sourceBlogAuthorInfo.profileIcon, // Full object
                      profileIconPath: sourceBlogAuthorInfo.profileIcon?.path
                    } : null,
                    pageDataKeys: pageMetadata ? Object.keys(pageMetadata) : 'N/A (array format)'
                  });
                }

                const hasMediaOrOffer = pageElements.some(el => el.type === 'media' || el.type === 'offer');

                // Both X and Y axes use Framer Motion on mobile for consistent,
                // spring-driven whoosh animations.
                const useFramerMotion = isMobile;
                const PageOuter = useFramerMotion ? MotionBox : Box;

                /**
                 * Per-page transition strategy for vertical (Y-axis) blog-to-blog
                 * navigation:
                 *
                 *  - When the jump is only 1 page (within a segment): animate
                 *    all pages as normal.
                 *  - When the jump spans multiple pages (blog-to-blog): only
                 *    the page we LEFT (prevPageRef) and the page we ARRIVED AT
                 *    (currentPage) get the smooth whoosh. Every other page gets
                 *    duration:0 so it teleports to its off-screen position
                 *    instantly and never flashes through the viewport.
                 *
                 * For X-axis (horizontal within-segment) navigation the jump is
                 * always exactly 1 page, so the standard whoosh is always used.
                 */
                const getPageTransition = () => {
                  if (prefersReducedMotion) return { type: 'tween', duration: 0.01, ease: 'linear' };
                  if (pageSlideAxis === 'x') return HORIZONTAL_PAGE_WHOOSH;
                  // Y-axis: skip animation for pages that are not part of the transition
                  const prevPage = prevPageRef.current;
                  const isLongJump = Math.abs(currentPage - prevPage) > 1;
                  const isParticipant = pageIndex === currentPage || pageIndex === prevPage;
                  if (isLongJump && !isParticipant) return { duration: 0 };
                  return VERTICAL_BLOG_WHOOSH;
                };

                const pageOuterMotionProps = useFramerMotion
                  ? {
                    initial: false,
                    // Always specify BOTH axes so Framer Motion resets whichever
                    // axis we're not currently sliding on back to 0.
                    animate: pageSlideAxis === 'x'
                      ? { x: `${(pageIndex - currentPage) * 100}%`, y: 0 }
                      : { y: `${(pageIndex - currentPage) * 100}%`, x: 0 },
                    transition: getPageTransition()
                  }
                  : {};

                return (
                  <PageOuter
                    key={pageIndex}
                    {...pageOuterMotionProps}
                    sx={{
                      position: isMobile ? 'absolute' : 'relative', // Absolute on mobile, relative on desktop
                      top: isMobile ? 0 : 'auto',
                      left: isMobile ? 0 : 'auto',
                      right: isMobile ? 0 : 'auto',
                      height: isMobile ? '100%' : 'auto', // Full height on mobile, auto on desktop
                      // On mobile, Framer Motion owns the transform; just hint
                      // the browser so it promotes the layer for compositing.
                      // On desktop, pages stack vertically via normal flow (no transform).
                      ...(!isMobile
                        ? { transform: 'none', transition: 'none' }
                        : { willChange: 'transform' }),
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: isMobile ? 0 : 3, // Add margin between pages on desktop
                      width: '100%',
                      alignItems: 'center' // Center the phone frame within the page
                    }}
                  >
                    <Paper sx={{
                      flex: 1,
                      backgroundColor: 'white',
                      borderRadius: 0, // Remove rounded corners
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)', // Deeper shadow for phone frame
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      // Phone frame styling
                      width: '375px', // Standard iPhone width
                      height: isMobile ? '100%' : 'auto', // Full height on mobile, auto on desktop
                      minHeight: isMobile ? '100%' : '667px', // Standard iPhone height
                      maxWidth: '375px',
                      margin: isMobile ? 0 : '20px auto', // No margin on mobile, center on desktop
                      // Phone frame border with realistic proportions
                      border: '12px solid #1a1a1a', // Dark phone frame
                      borderTopWidth: '24px', // Top border for notch area
                      borderBottomWidth: '24px', // Bottom border for home indicator
                      borderLeftWidth: '8px', // Side borders
                      borderRightWidth: '8px',
                      // Phone frame details
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '6px',
                        backgroundColor: '#1a1a1a',
                        borderRadius: '3px',
                        zIndex: 1
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '40px',
                        height: '4px',
                        backgroundColor: '#1a1a1a',
                        borderRadius: '2px',
                        zIndex: 1
                      },
                      '@media (max-width: 768px)': {
                        margin: 0,
                        padding: 0,
                        paddingTop: '0 !important',
                        marginTop: '0 !important',
                        width: '100%',
                        maxWidth: '100%',
                        height: '100%',
                        minHeight: '100%',
                        border: 'none',
                        boxShadow: 'none',
                        '&::before, &::after': {
                          display: 'none'
                        }
                      }
                    }}>
                      {/* Content area */}
                      <Box sx={{
                        flex: 1,
                        overflow: 'hidden',
                        display: hasMediaOrOffer ? 'flex' : 'block',
                        flexDirection: 'column',
                        backgroundColor: 'white',
                        p: 0, // No padding - let elements handle their own padding
                        pt: 0, // No top padding - padding is handled by element container
                        mt: 0, // Explicitly no top margin
                        '@media (max-width: 768px)': {
                          paddingTop: '0 !important',
                          marginTop: '0 !important'
                        }
                      }}>
                        {/* Affiliate Profile Image and Username - Show on first page of head blog, and on first page of each original blog in snake */}
                        {(() => {
                          // Only show profile image on first page of head blog, or on first page of each original blog within snake
                          const shouldShowProfileImage = (!isSnakePage && pageIndex === 0) || isFirstPageOfOriginalBlog;

                          if (!shouldShowProfileImage) {
                            return null;
                          }

                          // For snake pages, use the original blog's author; otherwise use the attached blog's author
                          const pageAuthor = isSnakePage && sourceBlogAuthorInfo
                            ? sourceBlogAuthorInfo
                            : blogPost?.author;

                          // Check if first element is media (image/video) to determine username color
                          const firstElement = pageElements && pageElements.length > 0 ? pageElements[0] : null;
                          const checkIsVideo = (el) => {
                            if (!el) return false;
                            if (el.type === 'video_with_button_overlay') return true;
                            if (el.type !== 'media') return false;
                            const src = el.videoFile || el.mediaUrl || el.videoSrc;
                            if (typeof src !== 'string') return false;
                            return src.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) || src.includes('youtube.com') || src.includes('youtu.be');
                          };
                          const isFirstElementVideo = checkIsVideo(firstElement);
                          const isFirstElementMedia = firstElement?.type === 'media';

                          // Debug logging for profile image
                          if (isSnakePage && pageIndex === (snakeMetadata?.snakeStartPageIndex || 0)) {
                            console.log(`🐍 Profile image check for snake page ${pageIndex}:`, {
                              hasSourceAuthorInfo: !!sourceBlogAuthorInfo,
                              sourceAuthorUsername: sourceBlogAuthorInfo?.username,
                              sourceAuthorProfileIcon: sourceBlogAuthorInfo?.profileIcon?.path,
                              usingBlogAuthor: !sourceBlogAuthorInfo,
                              blogAuthorUsername: blogPost?.author?.username,
                              pageAuthorUsername: pageAuthor?.username,
                              pageAuthorProfileIcon: pageAuthor?.profileIcon?.path
                            });
                          }

                          return pageAuthor ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                zIndex: 9999,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                pointerEvents: 'none', // Allow clicks to pass through
                              }}
                            >
                              {/* Profile Image */}
                              {pageAuthor.profileIcon?.path && (
                                <Box
                                  sx={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: '2px solid #e0e0e0',
                                    flexShrink: 0
                                  }}
                                >
                                  <img
                                    src={
                                      pageAuthor.profileIcon.path.startsWith('http')
                                        ? pageAuthor.profileIcon.path
                                        : `${config.SERVER_URL}${pageAuthor.profileIcon.path}`
                                    }
                                    alt={pageAuthor.username || 'Affiliate'}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                  />
                                </Box>
                              )}
                              {/* Username */}
                              {pageAuthor.username && (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    color: isFirstElementVideo ? '#ffffff' : '#333',
                                    textShadow: isFirstElementVideo ? '0 1px 3px rgba(0, 0, 0, 0.5)' : 'none' // Add text shadow for better visibility on videos
                                  }}
                                >
                                  {pageAuthor.username}
                                </Typography>
                              )}
                            </Box>
                          ) : null;
                        })()}

                        {pageElements.map((element, elementIndex) => {
                          // Only add padding when profile image is actually shown (first page of head blog or first page of each original blog in snake)
                          const hasProfileImage = ((!isSnakePage && pageIndex === 0) || isFirstPageOfOriginalBlog) && blogPost?.author;
                          const isFirstElement = elementIndex === 0;
                          const checkIsVideo = (el) => {
                            if (!el) return false;
                            if (el.type === 'video_with_button_overlay') return true;
                            if (el.type !== 'media') return false;
                            const src = el.videoFile || el.mediaUrl || el.videoSrc;
                            if (typeof src !== 'string') return false;
                            return src.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) || src.includes('youtube.com') || src.includes('youtu.be');
                          };
                          const isFirstElementVideo = isFirstElement && checkIsVideo(element);

                          // Override element styles to remove top padding when profile image is present
                          const elementWithNoTopPadding = hasProfileImage && isFirstElement ? {
                            ...element,
                            styles: {
                              ...element.styles,
                              paddingTop: 0,
                              pt: 0,
                              marginTop: 0,
                              mt: 0
                            }
                          } : element;

                          return (
                            <Box
                              key={element.id || elementIndex}
                              sx={{
                                mt: 0, // No top margin for any element
                                mb: 0, // No bottom margin
                                pt: 0, // No top padding by default
                                // Add top padding on first page when profile image is present to prevent overlap
                                // This padding exactly matches the profile image height (48px) with no gap
                                // However, if the first element is a video, we remove this padding to overlay the profile info
                                ...(hasProfileImage && isFirstElement && {
                                  paddingTop: isFirstElementVideo ? '0px' : '48px' // Profile image height - text starts directly below image, unless it's a video overlay
                                }),
                                // When first element without profile image, ensure no gap
                                ...(isFirstElement && !hasProfileImage && {
                                  paddingTop: '0 !important',
                                  marginTop: '0 !important'
                                }),
                                // When first element is offer, ensure it starts at container top
                                ...(isFirstElement && element.type === 'offer' && {
                                  marginTop: '0 !important',
                                  paddingTop: hasProfileImage ? '48px' : '0 !important'
                                })
                              }}
                            >
                              <BlogElementRenderer
                                element={elementWithNoTopPadding}
                                index={elementIndex}
                                isMobile={true}
                                affiliateId={pageAffiliateId}
                                offerId={pageOfferId}
                                pageNumber={pageIndex}
                                pageId={pageId}
                                blogPostId={blogPost._id}
                                sourceBlogId={sourceBlogId || null}
                                offerElementId={pageElements.find(el => el.type === 'offer')?.id || null}
                                sessionId={sessionTracking.getSessionId()}
                              />
                            </Box>
                          );
                        })}
                        {pageIndex === 0 && !isSnakePage && (
                          <AdSenseWidget />
                        )}
                      </Box>
                    </Paper>
                  </PageOuter>
                );
              })}

              {/* Outside page transforms: same screen position on every page (fixed on mobile = viewport) */}
              {showPrevNav && (
                <Box
                  sx={{
                    ...navRailCommon,
                    left: 'max(8px, env(safe-area-inset-left, 0px))',
                  }}
                >
                  <Button
                    type="button"
                    data-testid="blog-previous-page"
                    onClick={() => {
                      setPageSlideAxis('x');
                      if (currentPage > navSeg.segmentStart) {
                        setCurrentPage(currentPage - 1);
                      }
                    }}
                    sx={{
                      pointerEvents: 'auto',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                      minWidth: 'auto',
                      padding: 0,
                      borderRadius: '999px',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        opacity: 0.9,
                      },
                    }}
                  >
                    <img
                      src="/prevpage.png"
                      alt="Previous page"
                      style={{
                        display: 'block',
                        width: 'auto',
                        height: isMobile ? 200 : 240,
                        objectFit: 'contain',
                      }}
                    />
                  </Button>
                </Box>
              )}
              {showNextNav && (
                <Box
                  sx={{
                    ...navRailCommon,
                    right: 'max(8px, env(safe-area-inset-right, 0px))',
                  }}
                >
                  <Button
                    type="button"
                    data-testid="blog-next-page"
                    onClick={() => {
                      setPageSlideAxis('x');
                      if (currentPage < navSeg.segmentEnd) {
                        setCurrentPage(currentPage + 1);
                      }
                    }}
                    sx={{
                      pointerEvents: 'auto',
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                      minWidth: 'auto',
                      padding: 0,
                      borderRadius: '999px',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        opacity: 0.9,
                      },
                    }}
                  >
                    <img
                      src="/nextpage.png"
                      alt="Next page"
                      style={{
                        display: 'block',
                        width: 'auto',
                        height: isMobile ? 200 : 240,
                        objectFit: 'contain',
                      }}
                    />
                  </Button>
                </Box>
              )}
            </Box>
          );
  };

  // Check if first element is an offer to remove top padding
  const isFirstElementOffer = () => {
    if (!blogPost || !blogPost.content) return false;

    try {
      const parsedContent = JSON.parse(blogPost.content);

      // Check if content is in new pages format or old elements format
      if (Array.isArray(parsedContent) && parsedContent.length > 0 && parsedContent[0].id && parsedContent[0].elements) {
        // New format: pages structure - check first page's first element
        const firstPage = parsedContent[0];
        if (firstPage.elements && firstPage.elements.length > 0) {
          return firstPage.elements[0].type === 'offer';
        }
      } else if (Array.isArray(parsedContent) && parsedContent.length > 0) {
        // Old format: flat elements array - check first element
        return parsedContent[0].type === 'offer';
      }
    } catch (error) {
      console.error('Error parsing blog content:', error);
    }

    return false;
  };

  const renderBlogContent = () => {
    if (!blogPost || !blogPost.content) {
      return (
        <Typography variant="body1" color="text.secondary">
          No content available
        </Typography>
      );
    }

    // Use memoized pages
    if (pages.length > 0) {
      return renderMobilePagination(pages);
    }

    // Fallback to plain text if parsing failed
    return (
      <Typography variant="body1">
        {blogPost.content}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        overflowX: 'hidden',
        backgroundColor: isMobile ? 'white' : '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box sx={{
          width: isMobile ? '100%' : '414px',
          backgroundColor: 'white',
          boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
          borderRadius: isMobile ? 0 : '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8
        }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        overflowX: 'hidden',
        backgroundColor: isMobile ? 'white' : '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: 4
      }}>
        <Box sx={{
          width: isMobile ? '100%' : '414px',
          backgroundColor: 'white',
          boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
          borderRadius: isMobile ? 0 : '20px',
          p: 2
        }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body1" color="text.secondary">
            The blog post you're looking for doesn't exist or has been removed.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!blogPost) {
    return (
      <Box sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        overflowX: 'hidden',
        backgroundColor: isMobile ? 'white' : '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: 4
      }}>
        <Box sx={{
          width: isMobile ? '100%' : '414px',
          backgroundColor: 'white',
          boxShadow: isMobile ? 'none' : '0 0 20px rgba(0,0,0,0.1)',
          borderRadius: isMobile ? 0 : '20px',
          p: 2
        }}>
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
            Blog Post Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The blog post you're looking for doesn't exist or has been removed.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          body {
            margin: 0 !important;
            padding: 0 !important;
            overflow-x: hidden !important;
            overflow-y: hidden !important;
            height: 100vh !important;
            height: 100dvh !important; /* Dynamic viewport height for mobile */
          }
          html {
            margin: 0 !important;
            padding: 0 !important;
            overflow-x: hidden !important;
            overflow-y: hidden !important;
            height: 100vh !important;
            height: 100dvh !important; /* Dynamic viewport height for mobile */
          }
          #root {
            margin: 0 !important;
            padding: 0 !important;
            height: 100vh !important;
            height: 100dvh !important;
          }
          .App {
            margin: 0 !important;
            padding: 0 !important;
            height: 100vh !important;
            height: 100dvh !important;
          }
          /* Hide scrollbars for all elements */
          * {
            scrollbar-width: none !important; /* Firefox */
            -ms-overflow-style: none !important; /* IE and Edge */
          }
          *::-webkit-scrollbar {
            display: none !important; /* Chrome, Safari, Opera */
          }
          /* Mobile Chrome specific fixes */
          @supports (height: 100dvh) {
            body, html, #root, .App {
              height: 100dvh !important;
            }
          }
          /* Remove safe area insets */
          @supports (padding: env(safe-area-inset-bottom)) {
            body, html {
              padding-bottom: 0 !important;
              margin-bottom: 0 !important;
            }
          }
          /* Remove all borders from offer elements (only in production) */
          ${process.env.NODE_ENV === 'production' ? `
          [data-element-type="offer"], .offer-element, *[class*="offer"] {
            border: none !important;
            outline: none !important;
            border-style: none !important;
            border-width: 0 !important;
          }
          ` : ''}
          /* Remove dotted/dashed borders specifically */
          * {
            border-style: none !important;
          }
          
          /* Mobile-specific fixes for video and layout */
          @media (max-width: 768px) {
            body, html, #root, .App {
              margin: 0 !important;
              padding: 0 !important;
              height: 100vh !important;
              height: 100dvh !important;
              height: 100svh !important; /* Small viewport height - excludes browser UI */
              width: 100vw !important;
              max-width: 100vw !important;
              overflow-x: hidden !important;
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
            }
            
            /* Force first media element to have no top padding on mobile */
            .first-media-element {
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
            
            /* Remove top padding from all containers on mobile */
            .MuiPaper-root {
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
            
            /* Remove top padding from content container */
            .MuiBox-root[style*="flex"],
            .MuiBox-root[style*="overflow"] {
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
            
            /* Specifically target the content area Box */
            .MuiBox-root:has(.first-media-element) {
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
            
            /* Force full width for video elements in media containers only */
            .media-video-container video {
              width: 100vw !important;
              max-width: 100vw !important;
              height: auto !important;
              border-radius: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              object-fit: contain !important;
            }
            
            /* Force full width for image elements in media containers only */
            .media-image-container img {
              width: 100vw !important;
              max-width: 100vw !important;
              height: auto !important;
              border-radius: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              object-fit: contain !important;
            }
            
            /* Force full width for featured media elements */
            .featured-media-container video,
            .featured-media-container img {
              width: 100vw !important;
              max-width: 100vw !important;
              height: auto !important;
              border-radius: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              object-fit: contain !important;
            }
          }
        `}
      </style>
      <Box sx={{
        position: isMobile ? 'fixed' : 'relative',
        left: 0,
        right: 0,
        top: 0,
        bottom: isMobile ? 0 : 'auto',
        width: isMobile ? '100vw' : '100%', // Full width on desktop to accommodate phone frames
        height: isMobile ? '100dvh' : 'auto', // Auto height on desktop for scrolling
        maxWidth: isMobile ? '100vw' : '100%',
        minWidth: isMobile ? '100vw' : '100%',
        margin: isMobile ? 0 : '0 auto', // Center on desktop
        padding: 0,
        paddingBottom: 0, // Explicitly remove bottom padding
        overflowX: 'hidden',
        overflowY: isMobile ? 'hidden' : 'auto', // Allow scrolling on desktop
        backgroundColor: 'white', // Always white background
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        '@media (max-width: 768px)': {
          margin: 0,
          padding: 0,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          height: '100dvh',
          height: '100svh', // Small viewport height - excludes browser UI
          maxWidth: '100vw',
          minWidth: '100vw',
          position: 'fixed',
          overflowY: 'hidden'
        },
        '& *': {
          marginLeft: '0 !important',
          marginRight: '0 !important',
          boxSizing: 'border-box !important'
        }
      }}>
        {/* Affiliate Profile Image and Username Overlay - Show on all pages (head blog + snake) - Only show if not already shown in renderPages */}
        {false && currentPage === 0 && blogPost?.author && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              pointerEvents: 'none', // Allow clicks to pass through
            }}
          >
            {/* Profile Image */}
            {blogPost.author.profileIcon?.path && (
              <Box
                sx={{
                  width: isMobile ? '48px' : '56px',
                  height: isMobile ? '48px' : '56px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid rgba(255, 255, 255, 0.8)',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={
                    blogPost.author.profileIcon.path.startsWith('http')
                      ? blogPost.author.profileIcon.path
                      : `${config.SERVER_URL}${blogPost.author.profileIcon.path}`
                  }
                  alt={blogPost.author.username || 'Affiliate'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            )}
            {/* Username */}
            {blogPost.author.username && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(0, 0, 0, 0.85)',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(8px)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                  fontWeight: 600,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.5)'
                }}
              >
                {blogPost.author.username}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{
          mt: 0, // No top margin for both mobile and desktop
          width: '100%', // Full width for both mobile and desktop
          maxWidth: '100%',
          minWidth: '100%',
          margin: 0,
          padding: 0,
          paddingTop: 0, // No top padding - profile image is fixed, padding is handled by first element
          backgroundColor: 'white',
          boxShadow: 'none', // No shadow for both mobile and desktop
          borderRadius: 0, // No border radius for both mobile and desktop
          overflow: 'hidden',
          '@media (max-width: 768px)': {
            margin: 0,
            padding: 0,
            paddingTop: '0 !important', // No top padding - profile image is fixed
            top: 0,
            width: '100vw',
            maxWidth: '100vw',
            minWidth: '100vw',
            borderRadius: 0,
            boxShadow: 'none',
            marginTop: 0
          }
        }}>
          <Box sx={{
            mb: 2,
            width: '100%',
            margin: 0,
            padding: 0,
            minWidth: '100%',
            '@media (max-width: 768px)': {
              margin: 0,
              padding: 0,
              marginTop: 0,
              paddingTop: 0
            }
          }}>


            {/* Featured Image */}
            {blogPost.featuredImage && (
              <Box
                className="featured-media-container"
                sx={{
                  mb: 2,
                  textAlign: 'center',
                  px: isMobile ? 0 : 0.125, // Remove padding on mobile for full width
                  mx: isMobile ? '-16px' : 0 // Extend beyond container padding on mobile
                }}>
                {blogPost.featuredImage.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? (
                  <video
                    src={blogPost.featuredImage}
                    controls
                    poster={blogPost.attachments?.[0]?.thumbnailUrl || undefined}
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      aspectRatio: 'auto',
                      borderRadius: isMobile ? '0px' : '8px', // No border radius on mobile for full width
                      display: 'block'
                    }}
                  />
                ) : (
                  <img
                    src={blogPost.featuredImage}
                    alt={blogPost.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      aspectRatio: 'auto',
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                )}
              </Box>
            )}

            {/* Blog Content */}
            <Box sx={{
              flex: 1,
              overflow: isMobile ? 'auto' : 'visible', // Allow natural flow on desktop
              paddingBottom: '80px', // Space for bottom bar on both mobile and desktop
              px: 2, // 16px horizontal padding to match mobile preview
              pt: 0, // Remove all top padding
              '@media (max-width: 768px)': {
                px: 0, // Remove horizontal padding on mobile for full width
                paddingBottom: '80px', // Keep space for bottom bar
                pt: '0 !important', // Remove all top padding on mobile
                width: '100vw',
                maxWidth: '100vw',
                overflow: 'auto'
              }
            }}>
              {renderBlogContent()}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Next Blog hint and Fixed Bottom Bar were moved out here (previously
          nested inside the overflow:hidden content wrapper a few levels up)
          to fix an intermittent "bottom bar pushed up with a gap beneath it"
          bug on mobile. Both are position:fixed, which is spec'd to anchor to
          the true viewport regardless of ancestors — but mobile WebKit
          (Safari / the WKWebView used by in-app browsers like WhatsApp's) has
          a known quirk where position:fixed nested inside overflow:hidden
          ancestors, especially when one of THOSE is itself inside another
          position:fixed ancestor (exactly the nesting this page had: fixed
          outer wrapper > overflow:hidden content wrapper > this bar), can
          anchor to that ancestor's content box instead of the real viewport.
          Since that ancestor's height was auto/content-driven rather than
          locked to the viewport, the bar's effective position drifted with
          whatever height the content (video load timing, image decode, etc.)
          happened to render at — explaining why this wasn't 100% reproducible.
          CommentBottomSheet and the Info Bottom Sheet below already follow
          this same "fully outside the page wrapper" pattern for the same
          reason — this brings the bar and hint in line with that. */}

      {/* Next Blog hint — visible 1s after load for 2s, then disappears */}
      {showNextBlogHint && !commentSheetOpen && blogPost && blogPost.content && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '30px', // Overlaps halfway into the 60px bottom bar
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999, // Below bottom bar (10000) so bar covers lower half
            cursor: 'pointer',
            pointerEvents: 'auto',
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '40%': { transform: 'translateX(-50%) translateY(-10px)' },
              '60%': { transform: 'translateX(-50%) translateY(-5px)' },
            }
          }}
          onClick={() => {
            console.log('📱 [NAV] Next blog button clicked — navigating to next subcategory blog');
            handleNextSubcategory();
          }}
        >
          <img
            src="/next-blog.png"
            alt="Next Blog"
            style={{
              height: '60px',
              width: 'auto',
              display: 'block',
              filter: 'drop-shadow(0 -2px 10px rgba(0,0,0,0.3))'
            }}
          />
        </Box>
      )}

      {/* Fixed Bottom Bar */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: isMobile ? 0 : '50%',
        right: isMobile ? 0 : 'auto',
        transform: isMobile ? 'none' : 'translateX(-50%)',
        height: '60px',
        backgroundColor: '#000000',
        // Hidden while the comment sheet is open: the bar (and its oversized
        // "New idea" button) would otherwise cover the sheet's bottom controls.
        display: commentSheetOpen ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 10000,
        width: isMobile ? '100%' : '375px',
        px: 2,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        overflow: 'visible',
        '@media (max-width: 768px)': {
          width: '100%',
          left: 0,
          right: 0,
          transform: 'none'
        }
      }}>
        {/* Left Group: Logo + Comment */}
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', justifyContent: 'space-evenly', width: '40%' }}>
          {/* Icon 1: Logo */}
          <Box
            onClick={() => setInfoSheetOpen(true)}
            sx={{
              minWidth: '60px',
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <img
              src={siteConfig.iconLogo || LOGO_ICON_PATH}
              alt="Logo"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </Box>

          {/* Icon 2: Comment */}
          <Button
            variant="text"
            size="small"
            disableRipple
            disableFocusRipple
            sx={{
              backgroundColor: 'transparent !important',
              color: 'white',
              borderRadius: '50%',
              minWidth: '60px',
              width: '60px',
              height: '60px',
              padding: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
              },
              '&:active': {
                backgroundColor: 'transparent !important'
              },
              '&:focus': {
                backgroundColor: 'transparent !important'
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent !important'
              }
            }}
            onClick={() => setCommentSheetOpen(true)}
          >
            <img
              src={siteConfig.iconComment || '/comments_icon.png'}
              alt="Comment"
              style={{ width: '60px', height: '58px', objectFit: 'contain' }}
            />
          </Button>
        </Box>

        {/* Center: Join Icon - Absolutely positioned to center */}
        {/* Icon 3: Affiliate Signup */}
        <Button
          variant="text"
          size="small"
          disableRipple
          disableFocusRipple
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'transparent !important',
            color: 'white',
            borderRadius: '50%',
            minWidth: '60px',
            width: '60px',
            height: '60px',
            padding: 0,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
            },
            '&:active': {
              backgroundColor: 'transparent !important'
            },
            '&:focus': {
              backgroundColor: 'transparent !important'
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent !important'
            }
          }}
          onClick={handleAffiliateSignup}
          title="Become an Affiliate"
        >
          <img
            src={siteConfig.iconJoin || '/join_icon.png'}
            alt="Become an Affiliate"
            style={{ width: '60px', height: '60px', objectFit: 'contain' }}
          />
        </Button>

        {/* Right Group: Share + Next Topic */}
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', justifyContent: 'space-evenly', width: '40%' }}>
          {/* Icon 4: Send it */}
          <Button
            variant="text"
            size="small"
            disableRipple
            disableFocusRipple
            sx={{
              backgroundColor: 'transparent !important',
              color: 'white',
              borderRadius: '50%',
              minWidth: '60px',
              width: '60px',
              height: '60px',
              padding: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
              },
              '&:active': {
                backgroundColor: 'transparent !important'
              },
              '&:focus': {
                backgroundColor: 'transparent !important'
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent !important'
              }
            }}
            onClick={handleShareClick}
          >
            <img
              src={siteConfig.iconShare || '/sendIt-icon.png'}
              alt="Send it"
              style={{ width: '60px', height: '58px', objectFit: 'contain' }}
            />
          </Button>

          {/* Icon 5: Next topic */}
          <Button
            variant="text"
            size="small"
            disableRipple
            disableFocusRipple
            disabled={loadingNextSubcat || !blogPost?.categoryId || !blogPost?.subcategoryId}
            sx={{
              backgroundColor: 'transparent !important',
              color: 'white',
              borderRadius: '50%',
              minWidth: '140px',
              width: '140px',
              height: '140px',
              padding: 0,
              transform: 'translate(-15px, -35px)', // Move up and slightly left to stand out
              opacity: loadingNextSubcat ? 0.7 : 1,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
              },
              '&:active': {
                backgroundColor: 'transparent !important'
              },
              '&:focus': {
                backgroundColor: 'transparent !important'
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent !important'
              },
              '&:disabled': {
                opacity: 0.7
              }
            }}
            onClick={handleNextSubcategory}
            title="Next topic"
          >
            <img
              src={siteConfig.iconNextTopic || '/new_idea.png'}
              alt="Next topic"
              style={{
                width: '140px',
                height: '135px',
                objectFit: 'contain',
                opacity: loadingNextSubcat ? 0.5 : 1,
                transition: 'opacity 0.2s'
              }}
            />
          </Button>
        </Box>

        {/* <Button
      variant="text"
      size="small"
      sx={{
        backgroundColor: 'transparent',
        color: 'white',
        borderRadius: '50%',
        minWidth: '60px',
        width: '60px',
        height: '60px',
        padding: 0,
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
      }}
    >
      <Share sx={{ fontSize: '28px' }} />
    </Button> */}
      </Box>

      {/* Comment Bottom Sheet */}
      <CommentBottomSheet
        open={commentSheetOpen}
        onClose={() => setCommentSheetOpen(false)}
        blogPostId={blogPost?._id}
        blogTitle={blogPost?.title}
      />

      {/* Info Bottom Sheet */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          pointerEvents: infoSheetOpen ? 'auto' : 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          '@media (max-width: 768px)': {
            width: '100vw',
            height: '100vh',
            left: 0,
            right: 0,
            margin: 0,
            padding: 0
          }
        }}
      >
        <Backdrop
          open={infoSheetOpen}
          onClick={() => setInfoSheetOpen(false)}
          sx={{
            zIndex: 1300,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            width: '100%',
            height: '100%',
            '@media (max-width: 768px)': {
              width: '100vw',
              height: '100vh'
            }
          }}
        />

        <Slide direction="up" in={infoSheetOpen} mountOnEnter unmountOnExit>
          <Paper
            sx={{
              position: 'relative',
              height: 'auto',
              maxHeight: '85vh',
              zIndex: 1400,
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
              width: '375px',
              maxWidth: '375px',
              margin: 0,
              padding: 0,
              overflow: 'auto',
              '@media (max-width: 768px)': {
                borderRadius: '20px 20px 0 0',
                width: '100vw !important',
                maxWidth: '100vw !important',
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                margin: '0 !important',
                padding: '0 !important',
                boxSizing: 'border-box'
              }
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 64,
                px: 2,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#f8f9fa',
                position: 'relative'
              }}
            >
              <Box
                component="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  margin: 0,
                  padding: 0,
                  display: 'block'
                }}
              >
                About
              </Box>
              <IconButton
                onClick={() => setInfoSheetOpen(false)}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              {/* Logo */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <img
                  src={BIG_LOGO_PATH}
                  alt={BRAND_NAME}
                  style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                />
              </Box>

              {/* Main Description */}
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {siteConfig.aboutDescription ? (
                  siteConfig.aboutDescription
                ) : HAS_CUSTOM_ABOUT_DESCRIPTION ? (
                  ABOUT_DESCRIPTION
                ) : (
                  <>
                    <strong>{BRAND_NAME}</strong> works with health influencers to recommend products, healthcare professionals, and free tips to resolve your health issues.
                  </>
                )}
              </Typography>

              {/* For Brands */}
              <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>
                {siteConfig.aboutBrandsIntro || ABOUT_BRANDS_INTRO}
              </Typography>
              {(siteConfig.aboutContactEmail || ABOUT_CONTACT_EMAIL) ? (
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    mb: 3,
                    fontSize: '1.1rem'
                  }}
                >
                  {siteConfig.aboutContactEmail || ABOUT_CONTACT_EMAIL}
                </Typography>
              ) : null}

              {/* For Influencers */}
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                {siteConfig.aboutInfluencersCta || ABOUT_INFLUENCERS_CTA}
              </Typography>

              {/* CTA Button */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="text"
                  onClick={handleAffiliateSignup}
                  sx={{
                    padding: 0,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <img
                    src={JOIN_LOGO_PATH}
                    alt="Join"
                    style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                  />
                </Button>

              </Box>
            </Box>
          </Paper>
        </Slide>
      </Box>

      {/* Centered Loading Spinner */}
      {loadingNextSubcat && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            pointerEvents: 'auto'
          }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: 'white'
            }}
          />
        </Box>
      )}
    </>
  );
};

export default PublicBlogPage;
